import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppShell";
import { CdpObjectiveDashboard } from "@/components/CdpObjectiveView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  ChevronLeft, ChevronRight, Trophy, Users, Target, Flame, CheckCircle2,
  TrendingUp, CalendarDays, Crown, ArrowUpRight, CalendarClock, MapPin, Store, Gauge,
  ShieldAlert, ShieldCheck, PhoneCall, PhoneOff, AlertTriangle, ExternalLink, PhoneOutgoing,
  BadgeCheck, Sparkles, MicOff, HelpCircle, GitCompareArrows,
  Wallet, Receipt, TrendingDown, Save, Clock, Coins, ChevronDown, Brain, Plus, X,
  Pencil, Trash2, SlidersHorizontal, RotateCcw, CalendarRange, Check,
  GraduationCap, CalendarPlus, Eraser, MoreHorizontal, Loader2, CalendarOff, UserCheck, Footprints,
  Mail, Download, Send, FileSpreadsheet,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CockpitTelephonie } from "@/components/CockpitTelephonie";
import { CdpDiagnostic } from "@/components/CdpDiagnostic";
import { toast } from "sonner";

function toISODate(x) {
  // Formate en YYYY-MM-DD à partir des composantes LOCALES (aucun décalage UTC).
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const d = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = lundi
  x.setDate(x.getDate() - day);
  return toISODate(x);
}
function addDays(iso, n) {
  const x = new Date(iso + "T00:00:00");
  x.setDate(x.getDate() + n);
  return toISODate(x);
}
function addMonthsIso(iso, n) {
  const x = new Date((iso || todayIso()) + "T12:00:00");
  x.setMonth(x.getMonth() + n);
  return toISODate(x);
}
function addYearsIso(iso, n) {
  const x = new Date((iso || todayIso()) + "T12:00:00");
  x.setFullYear(x.getFullYear() + n);
  return toISODate(x);
}
const todayIso = () => toISODate(new Date());
function frDay(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}
function frShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}
function frDateFull(iso) {
  // « lun. 4 mai 2026 » — date longue avec année pour les passages prévus.
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}
function frDateChip(iso) {
  // « 5 oct. » — jour + mois court, pour la liste des dates de passage sur la carte.
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch { return iso; }
}
// Durée d'appel lisible : « 45 min » ou « 1 h 05 ».
function frMinutes(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} h ${String(r).padStart(2, "0")}` : `${h} h`;
}
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(ym, n) {
  const [y, m] = (ym || currentMonth()).split("-").map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function frMonth(ym) {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  try {
    return new Date(y, m - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch { return ym; }
}
function eur(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
}

const card = "rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

const STATUS_TONE = {
  ferme: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  realise: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  confirme: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8]",
  reserve: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8]",
  au_crayon: "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
  propose: "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
  annule: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
};

export default function SuiviCDP() {
  const { user } = useAuth();
  // Le Superviseur (consultation seule) N'A PAS accès à la Facturation : l'onglet est masqué
  // et la vue est verrouillée (le backend bloque aussi /performance/cdp-billing pour ce profil).
  const isSuperviseur = user?.role === "superviseur";
  const [view, setView] = useState("week");
  const safeView = isSuperviseur && view === "facturation" ? "week" : view;
  return (
    <>
      <PageHeader
        title="Suivi CDP"
        subtitle="Performance de booking par chargé de développement partenarial."
        action={
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="suivi-view-toggle">
            <ToggleBtn active={safeView === "week"} onClick={() => setView("week")} icon={TrendingUp} label="Semaine" testId="view-week" />
            <ToggleBtn active={safeView === "day"} onClick={() => setView("day")} icon={CalendarClock} label="Par jour" testId="view-day" />
            <ToggleBtn active={safeView === "pilotage"} onClick={() => setView("pilotage")} icon={Gauge} label="Pilotage" testId="view-pilotage" />
            <ToggleBtn active={safeView === "controle"} onClick={() => setView("controle")} icon={ShieldCheck} label="Contrôle" testId="view-controle" />
            <ToggleBtn active={safeView === "closing"} onClick={() => setView("closing")} icon={GitCompareArrows} label="Closing" testId="view-closing" />
            {!isSuperviseur && (
              <ToggleBtn active={safeView === "facturation"} onClick={() => setView("facturation")} icon={Wallet} label="Facturation" testId="view-facturation" />
            )}
          </div>
        }
      />
      <div className="mx-auto max-w-[1280px] px-4 py-5 sm:px-6 lg:px-8" data-testid="suivi-cdp-page">
        {safeView === "week" ? <WeekView /> : safeView === "day" ? <DailyView /> : safeView === "pilotage" ? <CockpitTelephonie /> : safeView === "controle" ? <ConfirmationAudit /> : safeView === "closing" ? <PropositionsClosingView /> : <FacturationView />}
      </div>
    </>
  );
}

const ToggleBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
    <Icon className="h-4 w-4" />{label}
  </button>
);

/* ---------------------------------------------------- CLOSING DES PROPOSITIONS */
const PROP_PERIODS = [
  { key: "all", label: "Tout" },
  { key: "12m", label: "12 mois" },
  { key: "3m", label: "3 mois" },
  { key: "month", label: "Ce mois" },
];

// Tonalité (sémantique) du taux de closing : bon / moyen / faible.
function tauxTone(t) {
  if (t >= 0.5) return { bg: "bg-[rgba(31,157,102,0.12)]", text: "text-[#1B7F53]" };
  if (t >= 0.25) return { bg: "bg-[rgba(214,158,46,0.16)]", text: "text-[#8A5A0B]" };
  return { bg: "bg-[rgba(214,69,69,0.12)]", text: "text-[#9B2226]" };
}
const pctFmt = (x) => `${Math.round((x || 0) * 100)}%`;

function PropositionsClosingView() {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/performance/cdp/propositions", { params: { period } });
      setData(d);
    } finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <div data-testid="prop-closing-view">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-bold">Taux de closing des propositions de dates</h2>
          <p className="text-[12px] text-muted-ink">Mails « Proposition de date » envoyés aux responsables → le directeur clique pour accepter (date bookée).</p>
          {data?.test_excluded > 0 && (
            <p className="mt-1 inline-flex items-center gap-1 rounded-md bg-[rgba(214,158,46,0.14)] px-2 py-0.5 text-[11px] font-semibold text-[#8A5A0B]" data-testid="prop-test-excluded">
              <AlertTriangle className="h-3 w-3" /> {data.test_excluded} enregistrement{data.test_excluded > 1 ? "s" : ""} de test exclu{data.test_excluded > 1 ? "s" : ""} (robots / e-mails bidons)
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="prop-period-toggle">
          {PROP_PERIODS.map((p) => (
            <button key={p.key} data-testid={`prop-period-${p.key}`} onClick={() => setPeriod(p.key)}
              className={`rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${period === p.key ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-[var(--radius)]" />)}</div>
          <Skeleton className="h-72 w-full rounded-[var(--radius)]" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="prop-summary">
            <SummaryTile icon={GitCompareArrows} label="Propositions" value={t.propositions} />
            <SummaryTile icon={CheckCircle2} label="Dates confirmées" value={t.confirmes} accent />
            <SummaryTile icon={Target} label="Taux de closing" value={pctFmt(t.taux_closing)} />
            <SummaryTile icon={CalendarClock} label="En attente / négo" value={(t.en_attente || 0) + (t.contre_proposition || 0)} />
          </div>

          <div className={`${card} overflow-hidden`} data-testid="prop-leaderboard">
            <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-2.5">
              <h3 className="flex items-center gap-1.5 text-sm font-bold"><GitCompareArrows className="h-4 w-4 text-[var(--shariti-orange)]" />Closing par CDP</h3>
              <span className="text-[11px] text-muted-ink">Trié par taux de closing</span>
            </div>
            {(!data.cdps || data.cdps.length === 0) ? (
              <div className="p-8 text-center text-sm text-muted-ink" data-testid="prop-empty">Aucune proposition de dates sur cette période.</div>
            ) : (
              <div className="divide-y divide-[var(--border-soft)]">
                {data.cdps.map((r) => {
                  const tone = tauxTone(r.taux_closing);
                  return (
                    <div key={r.cdp_id || r.name} className="flex items-center gap-3 px-4 py-3" data-testid={`prop-row-${r.cdp_id || r.name}`}>
                      <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: r.color || "#c9c9c9" }} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold">{r.name}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <Progress value={Math.round((r.taux_closing || 0) * 100)} className="h-2 w-28 sm:w-36" />
                          <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${r.propositions ? `${tone.bg} ${tone.text}` : "bg-[var(--surface-2)] text-muted-ink"}`}>{r.propositions ? pctFmt(r.taux_closing) : "—"}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-ink">
                          {r.propositions ? (
                            <>
                              <span>{r.confirmes}/{r.propositions} confirmée{r.confirmes > 1 ? "s" : ""} par le directeur</span>
                              {r.en_attente > 0 && <span>· {r.en_attente} en attente</span>}
                              {r.contre_proposition > 0 && <span>· {r.contre_proposition} contre-prop.</span>}
                              {r.autre > 0 && <span>· {r.autre} confirmé(s) sans preuve de clic</span>}
                              {r.renouvellements > 0 && <span>· dont {r.renouvellements} renouv.</span>}
                            </>
                          ) : (
                            <span className="italic">Aucune proposition de date sur la période</span>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        {r.propositions ? (
                          <>
                            <div className="text-lg font-extrabold leading-none tabular-nums">{r.confirmes}</div>
                            <div className="text-[10px] text-muted-ink">sur {r.propositions}</div>
                          </>
                        ) : (
                          <div className="text-lg font-extrabold leading-none tabular-nums text-muted-ink">—</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <p className="mt-3 text-[11px] leading-relaxed text-muted-ink">
            « Closing » = le responsable a <b>cliqué pour accepter</b> les dates depuis le mail « Proposition de date » (preuve horodatée via la page publique).
            Les propositions « en attente » (aucune réponse) et « contre-propositions » ne sont pas des closings.
            Les enregistrements de test (robots / e-mails bidons) sont automatiquement exclus.
          </p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ SEMAINE */
function WeekView() {
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillId, setDrillId] = useState(null);
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/performance/cdp/overview", { params: { week: monday } });
      setData(d);
    } finally { setLoading(false); }
  }, [monday]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (cdpId) => {
    setDrillId(cdpId); setDrill(null); setDrillLoading(true);
    try {
      const { data: d } = await api.get(`/performance/cdp/${cdpId}`, { params: { week: monday } });
      setDrill(d);
    } finally { setDrillLoading(false); }
  };

  const target = data?.target ?? 3;
  const champion = data?.champion;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm font-semibold text-muted-ink">Objectif par défaut : {target} spots/jour</span>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonday(addDays(monday, -7))} data-testid="cdp-week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="px-2 text-sm font-bold" data-testid="cdp-week-label">Semaine {data?.week_number ?? "—"}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonday(addDays(monday, 7))} data-testid="cdp-week-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {loading ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-[var(--radius)]" />)}</div>
          <Skeleton className="h-72 w-full rounded-[var(--radius)]" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="cdp-summary">
            <SummaryTile icon={Users} label="CDP actifs" value={`${data.totals.active_cdp}/${data.totals.cdp_count}`} />
            <SummaryTile icon={CalendarDays} label="Spots bookés (sem.)" value={data.totals.week_total} accent />
            <SummaryTile icon={Target} label="Jours à l'objectif" value={data.totals.goal_hits} />
            <SummaryTile icon={Trophy} label="Objectif / jour" value={`${target} spots`} />
          </div>

          {champion && champion.week_total > 0 && (
            <div className="mb-4 flex items-center gap-4 overflow-hidden rounded-[var(--radius)] border border-[rgba(238,114,46,0.3)] bg-[rgba(238,114,46,0.06)] p-4" data-testid="cdp-champion">
              <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[var(--shariti-orange)] text-white shadow-md"><Crown className="h-7 w-7" /></span>
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A3A10]">Champion de la semaine</div>
                <div className="flex items-center gap-2 text-lg font-extrabold">
                  <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: champion.cdp.color }} />
                  {champion.cdp.name}
                </div>
                <div className="text-sm text-muted-ink">{champion.week_total} spots bookés · {champion.days_goal_hit} jour(s) à l'objectif · {champion.success_rate}% de réussite</div>
              </div>
            </div>
          )}

          <div className={`${card} overflow-hidden`} data-testid="cdp-leaderboard">
            <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-2.5">
              <h3 className="flex items-center gap-1.5 text-sm font-bold"><TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" />Classement des CDP</h3>
              <span className="text-[11px] text-muted-ink">Cliquez sur une ligne pour le détail</span>
            </div>
            <div className="divide-y divide-[var(--border-soft)]">
              {data.rows.map((r) => {
                const inactive = r.week_total === 0;
                return (
                  <button key={r.cdp.id} onClick={() => openDrill(r.cdp.id)} data-testid={`cdp-row-${r.cdp.id}`}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--surface-2)] ${inactive ? "opacity-55" : ""}`}>
                    <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-extrabold ${r.rank <= 3 && !inactive ? "bg-[var(--shariti-orange)] text-white" : "bg-[var(--surface-2)] text-muted-ink"}`}>{r.rank}</span>
                    <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: r.cdp.color }} />
                    <div className="min-w-0 flex-[1.4]">
                      <div className="break-words text-sm font-bold">{r.cdp.name}</div>
                      <div className="text-[11px] text-muted-ink">{r.active_days} jour(s) actif(s) · {r.avg_per_active_day}/jour · obj. {r.target ?? target}</div>
                    </div>
                    <div className="hidden items-end gap-0.5 sm:flex" title="Spots bookés par jour (lun→sam)">
                      {r.per_day.map((d) => (
                        <span key={d.date} className="w-2 rounded-sm"
                          style={{ height: `${Math.max(4, Math.min(28, d.count * 5))}px`, background: d.hit ? "var(--affinity-strong)" : d.count > 0 ? "var(--shariti-orange)" : "var(--border-soft)" }} />
                      ))}
                    </div>
                    <div className="flex-1 px-2">
                      <div className="mb-0.5 flex items-center justify-between text-[10px] font-semibold text-muted-ink"><span>Réussite</span><span>{r.success_rate}%</span></div>
                      <Progress value={r.success_rate} className="h-1.5" />
                    </div>
                    <div className="w-14 text-center">
                      <div className="text-lg font-extrabold tabular-nums leading-none">{r.week_total}</div>
                      <div className="text-[9px] uppercase text-muted-ink">spots</div>
                    </div>
                    <div className="flex w-24 shrink-0 items-center justify-end gap-1.5">
                      {r.current_streak > 0 && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-[rgba(238,114,46,0.1)] px-1.5 py-0.5 text-[10px] font-bold text-[#8A3A10]"><Flame className="h-3 w-3" />{r.current_streak}j</span>
                      )}
                      {r.today_hit ? (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#146B3A]"><CheckCircle2 className="h-3 w-3" />Auj.</span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink">{r.today_count}/{r.target ?? target}</span>
                      )}
                      <ArrowUpRight className="h-3.5 w-3.5 text-muted-ink" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      <Sheet open={!!drillId} onOpenChange={(o) => { if (!o) { setDrillId(null); setDrill(null); } }}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[560px]" data-testid="cdp-drill-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="flex items-center gap-2">
              {drill?.cdp && <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: drill.cdp.color }} />}
              {drill?.cdp?.name || "Détail CDP"}
            </SheetTitle>
            <SheetDescription>Performance détaillée</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {drillLoading || !drill ? (
              <div className="space-y-3"><Skeleton className="h-44 w-full rounded-[var(--radius)]" /><Skeleton className="h-32 w-full rounded-[var(--radius)]" /></div>
            ) : (
              <>
                <button
                  onClick={() => setDiagOpen(true)}
                  data-testid="cdp-drill-diagnostic-btn"
                  className="mb-4 flex w-full items-center gap-3 rounded-2xl border border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.06)] px-4 py-3 text-left transition-colors hover:bg-[rgba(238,114,46,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--shariti-orange)] text-white"><Brain className="h-5 w-5" /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-extrabold text-ink">Diagnostic IA</span>
                    <span className="block text-[11px] text-muted-ink">Pourquoi ce CDP ne décroche pas ? Analyse des appels en 1 clic.</span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
                </button>
                <CdpObjectiveDashboard data={drill} compact />
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <CdpDiagnostic
        cdpId={drillId}
        cdpName={drill?.cdp?.name}
        open={diagOpen}
        onOpenChange={setDiagOpen}
        initialPeriod="week"
        initialDate={monday}
      />
    </>
  );
}

/* -------------------------------------------------------------- PAR JOUR / SEMAINE / MOIS / ANNÉE */
const HORIZONS = [["day", "Jour"], ["week", "Semaine"], ["month", "Mois"], ["year", "Année"]];

function DailyView() {
  const [period, setPeriod] = useState("day");
  const [anchor, setAnchor] = useState(null); // ISO ; null => le backend ancre sur le dernier jour actif (mode Jour)
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (period === "day") {
        // Mode Jour : on conserve l'ancrage historique sur le dernier jour de booking actif.
        const { data: d } = await api.get("/performance/daily-bookings", { params: anchor ? { day: anchor } : {} });
        setData({ ...d, period: "day", range: { start: d.day, end: d.day, label_fr: null } });
        if (!anchor && d.day) setAnchor(d.day);
      } else {
        const a = anchor || todayIso();
        const { data: d } = await api.get("/performance/bookings-overview", { params: { period, anchor: a } });
        setData(d);
        if (!anchor) setAnchor(a);
      }
    } finally { setLoading(false); }
  }, [period, anchor]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, anchor]);

  const shift = (n) => {
    const base = anchor || data?.range?.start || todayIso();
    if (period === "day") setAnchor(addDays(base, n));
    else if (period === "week") setAnchor(addDays(base, n * 7));
    else if (period === "month") setAnchor(addMonthsIso(base, n));
    else setAnchor(addYearsIso(base, n));
  };

  const isDay = period === "day";
  const rawLabel = isDay ? frDay(data?.range?.start || data?.day) : (data?.range?.label_fr || "…");
  const label = rawLabel ? rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1) : rawLabel;
  const subtitle = isDay
    ? "Enseignes décrochées ce jour-là (par jour de booking). 1 enseigne = 1 spot, quel que soit le nombre de dates."
    : "Enseignes décrochées sur la période (par jour de booking). 1 enseigne = 1 spot, quel que soit le nombre de dates.";

  return (
    <>
      {/* Sélecteur d'horizon : Jour / Semaine / Mois / Année (même style de cartes) */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="daily-horizon-toggle">
          {HORIZONS.map(([p, lbl]) => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`daily-horizon-${p}`}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition-colors ${period === p ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>{lbl}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {isDay && (
            <Input type="date" value={anchor || data?.day || ""} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="h-9 w-[160px]" data-testid="daily-date-input" />
          )}
          <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="daily-prev" title="Période précédente"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setAnchor(todayIso())} data-testid="daily-today">Aujourd'hui</Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="daily-next" title="Période suivante"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Libellé d'horizon lisible (long en français) */}
      <div className="mb-4 min-w-0">
        <div className="text-base font-extrabold text-ink" data-testid="daily-date-label">{label}</div>
        <div className="text-[11px] text-muted-ink">{subtitle}</div>
      </div>

      {loading || !data ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-[var(--radius)]" />)}</div>
          <Skeleton className="h-64 w-full rounded-[var(--radius)]" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="daily-summary">
            <SummaryTile icon={CalendarDays} label="Enseignes bookées" value={data.totals.total_spots} accent />
            <SummaryTile icon={Users} label="CDP actifs" value={data.totals.active_cdp} />
            <SummaryTile icon={Target} label="Objectifs atteints" value={data.totals.goals_hit} />
            <SummaryTile icon={PhoneOutgoing} label={isDay ? "Appels du jour" : "Appels"} value={data.totals.calls ?? 0} />
            <SummaryTile icon={Clock} label="Temps d'appel" value={frMinutes(data.totals.call_minutes)} />
            <SummaryTile icon={Footprints} label="Rebookings terrain" value={data.totals.field_rebookings ?? 0} />
          </div>

          {data.totals.assoc_breakdown?.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="daily-assoc-breakdown">
              <span className="text-[11px] font-semibold uppercase text-muted-ink">Par association :</span>
              {data.totals.assoc_breakdown.map((a) => (
                <span key={a.acronym} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-white px-2.5 py-1 text-xs font-semibold">
                  {a.acronym} <span className="text-muted-ink">· {a.count}</span>
                </span>
              ))}
            </div>
          )}

          {data.cdp_blocks.length === 0 ? (
            <div className={`${card} flex flex-col items-center justify-center gap-2 py-16 text-center`} data-testid="daily-empty">
              <CalendarClock className="h-8 w-8 text-muted-ink" />
              <p className="text-sm font-semibold">{isDay ? "Aucune réservation CDP ce jour-là" : "Aucune réservation CDP sur cette période"}</p>
              <p className="text-xs text-muted-ink">Choisissez une autre période ou revenez à aujourd'hui.</p>
            </div>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2" data-testid="daily-cdp-grid">
              {data.cdp_blocks.map((b, idx) => <CdpDayCard key={b.cdp.id || `x${idx}`} block={b} />)}
            </div>
          )}

          {/* Rebookings TERRAIN : NON attribués aux CDP (faits par le terrain) */}
          {(data.field_rebookings?.length > 0) && (
            <div className="mt-6" data-testid="daily-field-rebookings">
              <div className="mb-2 flex items-center gap-2">
                <Footprints className="h-4 w-4 text-[#7C3AED]" />
                <h3 className="text-sm font-extrabold text-ink">Rebookings terrain</h3>
                <span className="text-[11px] text-muted-ink">— rebookés par le terrain, non comptés à l'actif d'un CDP</span>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {data.field_rebookings.map((g, i) => <FieldRebookingCard key={`${g.rebooked_by}-${i}`} group={g} />)}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

// Carte d'un REBOOKING TERRAIN : regroupe les enseignes rebookées par une même
// personne du terrain. Style violet pour bien la distinguer des cartes CDP.
function FieldRebookingCard({ group }) {
  const initials = (group.rebooked_by || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
  // Regroupe les dates par enseigne (comme les cartes CDP)
  const bySpot = new Map();
  for (const bk of group.bookings) {
    const key = bk.spot_id || bk.spot_name;
    if (!bySpot.has(key)) bySpot.set(key, { spot_name: bk.spot_name, ville: bk.ville, acr: bk.association_acronym, acrColor: bk.association_color, dates: [] });
    bySpot.get(key).dates.push({ d: bk.intervention_date, status: bk.status, label: bk.status_label });
  }
  const groups = [...bySpot.values()];
  return (
    <div className={`${card} overflow-hidden`} data-testid={`daily-field-${group.rebooked_by}`} style={{ borderColor: "rgba(124,58,237,0.35)" }}>
      <div className="flex items-center gap-3 border-b border-[var(--border-soft)] p-3" style={{ borderLeft: "4px solid #7C3AED" }}>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[rgba(124,58,237,0.14)] text-sm font-extrabold text-[#7C3AED]">{initials}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="break-words font-bold">{group.rebooked_by}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(124,58,237,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#7C3AED]"><Footprints className="h-3 w-3" />Terrain</span>
            {group.pending_validation && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(180,83,9,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#B45309]">À valider</span>
            )}
          </div>
          <div className="text-[11px] text-muted-ink">{group.count} enseigne{group.count > 1 ? "s" : ""} rebookée{group.count > 1 ? "s" : ""} · {group.reservations_count} date{group.reservations_count > 1 ? "s" : ""}</div>
        </div>
        <div className="shrink-0 text-right" title="Non attribué à un CDP">
          <div className="text-[10px] font-bold uppercase leading-tight text-[#7C3AED]">Hors CDP</div>
        </div>
      </div>
      <div className="max-h-[340px] divide-y divide-[var(--border-soft)] overflow-y-auto">
        {groups.map((g, i) => (
          <div key={`${g.spot_name}-${i}`} className="flex items-start gap-2 px-3 py-2 text-sm" data-testid={`daily-field-spot-${i}`}>
            <Store className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {g.acr && <span className="shrink-0 rounded px-1 py-0.5 text-[10px] font-bold" style={{ background: `${g.acrColor || "#7C3AED"}22`, color: g.acrColor || "#5B21B6" }}>{g.acr}</span>}
                <span className="break-words font-semibold">{g.spot_name}</span>
              </div>
              {g.ville && <div className="mt-0.5 flex items-start gap-0.5 break-words text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{g.ville}</div>}
              <div className="mt-1 flex flex-wrap gap-1">
                {g.dates.sort((a, b) => (a.d || "").localeCompare(b.d || "")).map((dt, j) => (
                  <span key={j} title={dt.label} className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[dt.status] || "bg-[var(--surface-2)] text-muted-ink"}`}>
                    <CalendarDays className="h-3 w-3" />{frShort(dt.d)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CdpDayCard({ block }) {
  const color = block.cdp.color || "#94A3B8";
  const initials = (block.cdp.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("");
  const pct = block.target ? Math.min(100, Math.round((block.count / block.target) * 100)) : 0;

  // Regroupement par spot : un spot listé une seule fois, avec toutes ses dates d'intervention.
  const groups = [];
  const idx = {};
  for (const bk of block.bookings) {
    const key = bk.spot_id || bk.spot_name;
    if (idx[key] === undefined) {
      idx[key] = groups.length;
      groups.push({ spot_name: bk.spot_name, ville: bk.ville, acr: bk.association_acronym, acrColor: bk.association_color, dates: [] });
    }
    groups[idx[key]].dates.push({ d: bk.intervention_date, status: bk.status, label: bk.status_label });
  }
  groups.forEach((g) => g.dates.sort((a, b) => (a.d || "").localeCompare(b.d || "")));

  return (
    <div className={`${card} overflow-hidden`} data-testid={`daily-cdp-${block.cdp.id || "none"}`}>
      <div className="flex items-center gap-3 border-b border-[var(--border-soft)] p-3" style={{ borderLeft: `4px solid ${color}` }}>
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full text-sm font-extrabold" style={{ background: color, color: "#181818" }}>{initials}</span>
        <div className="min-w-0 flex-1">
          <div className="break-words font-bold">{block.cdp.name}{block.cdp.unknown && <span className="ml-1 text-[10px] font-normal text-muted-ink">(non attribué)</span>}</div>
          <div className="text-[11px] text-muted-ink">{block.count} enseigne{block.count > 1 ? "s" : ""} · {block.reservations_count} date{block.reservations_count > 1 ? "s" : ""} réservée{block.reservations_count > 1 ? "s" : ""}</div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
            <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%`, background: block.hit ? "var(--affinity-strong, #1F9D55)" : "var(--shariti-orange)" }} />
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-lg font-extrabold leading-none tabular-nums">{block.count}<span className="text-xs font-semibold text-muted-ink">/{block.target}</span></div>
          {block.hit ? (
            <span className="mt-1 inline-flex items-center gap-0.5 rounded-full bg-[rgba(31,157,85,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#146B3A]" data-testid="daily-goal-hit"><CheckCircle2 className="h-3 w-3" />Objectif</span>
          ) : (
            <span className="mt-1 inline-flex items-center rounded-full bg-[var(--surface-2)] px-1.5 py-0.5 text-[10px] font-semibold text-muted-ink" data-testid="daily-goal-miss">{block.target - block.count} restant(s)</span>
          )}
        </div>
      </div>
      {/* Activité téléphonique du CDP CE JOUR-LÀ : appels passés + temps d'appel */}
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--border-soft)] bg-[var(--surface-2)]/60 px-3 py-2" data-testid={`daily-calls-${block.cdp.id || "none"}`}>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[11px] font-bold text-ink ring-1 ring-inset ring-[var(--border-soft)]" title="Appels passés ce jour-là">
          <PhoneOutgoing className="h-3.5 w-3.5 text-[var(--shariti-orange)]" />
          {block.calls || 0} appel{(block.calls || 0) > 1 ? "s" : ""}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-2 py-1 text-[11px] font-bold text-ink ring-1 ring-inset ring-[var(--border-soft)]" title="Temps d'appel cumulé ce jour-là">
          <Clock className="h-3.5 w-3.5 text-[#2F6FED]" />
          {frMinutes(block.call_minutes)} d'appel
        </span>
        {(block.calls || 0) > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-ink" title="Appels aboutis (décrochés)">
            <PhoneCall className="h-3 w-3 text-[#146B3A]" />{block.calls_answered || 0} abouti{(block.calls_answered || 0) > 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="max-h-[340px] divide-y divide-[var(--border-soft)] overflow-y-auto">
        {groups.length === 0 ? (
          <div className="flex items-center gap-2 px-3 py-4 text-[12px] text-muted-ink" data-testid="daily-no-booking">
            <Store className="h-4 w-4 shrink-0 text-muted-ink/60" />
            Aucun emplacement booké ce jour-là — activité téléphonique uniquement.
          </div>
        ) : groups.map((g, i) => (
          <div key={`${g.spot_name}-${i}`} className="flex items-start gap-2 px-3 py-2 text-sm" data-testid={`daily-spot-${i}`}>
            <Store className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-ink" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {g.acr && (
                  <span className="shrink-0 rounded px-1 py-0.5 text-[10px] font-bold" style={{ background: `${g.acrColor || "#EE722E"}22`, color: g.acrColor || "#8A3A10" }}>{g.acr}</span>
                )}
                <span className="break-words font-semibold">{g.spot_name}</span>
              </div>
              {g.ville && <div className="mt-0.5 flex items-start gap-0.5 break-words text-[11px] text-muted-ink"><MapPin className="mt-0.5 h-3 w-3 shrink-0" />{g.ville}</div>}
              <div className="mt-1 flex flex-wrap gap-1">
                {g.dates.map((dt, j) => (
                  <span key={j} title={dt.label}
                    className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_TONE[dt.status] || "bg-[var(--surface-2)] text-muted-ink"}`}>
                    <CalendarDays className="h-3 w-3" />{frShort(dt.d)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SummaryTile = ({ icon: Icon, label, value, accent }) => (
  <div className={`${card} flex items-center gap-3 p-3`}>
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[var(--surface-2)] text-[var(--shariti-orange)]"><Icon className="h-4 w-4" /></span>
    <div>
      <div className="text-xl font-extrabold tabular-nums" style={{ color: accent ? "var(--shariti-orange)" : "var(--shariti-ink)" }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase leading-tight text-muted-ink">{label}</div>
    </div>
  </div>
);


/* ------------------------------------------------------- FACTURATION / RENTABILITÉ */
const BILLING_LABEL = {
  hourly: { label: "À l'heure", tone: "bg-[rgba(47,111,237,0.12)] text-[#1D4ED8]", icon: Clock },
  per_enseigne: { label: "À l'enseigne", tone: "bg-[rgba(238,114,46,0.12)] text-[#8A3A10]", icon: Store },
  forfait: { label: "Forfait", tone: "bg-[rgba(233,184,0,0.2)] text-[#5A4200]", icon: Coins },
};

function FacturationView() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refValue, setRefValue] = useState("");
  const [savingRef, setSavingRef] = useState(false);
  const [drill, setDrill] = useState(null); // ligne CDP → panneau Jour par jour
  const [settingsEdit, setSettingsEdit] = useState(null); // ligne CDP → réglages (mode, tarifs, contrat)

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/performance/cdp-billing", { params: { month } });
      setData(d);
      setRefValue(String(d.reference_value_per_enseigne ?? ""));
    } catch {
      toast.error("Impossible de charger la facturation");
    } finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const saveRef = async () => {
    const num = Number(refValue);
    if (!(num >= 0)) { toast.error("Valeur de référence invalide"); return; }
    setSavingRef(true);
    try {
      await api.put("/performance/cdp-billing/settings", { reference_value_per_enseigne: num });
      toast.success("Valeur de référence mise à jour");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement"); }
    finally { setSavingRef(false); }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || { enseignes: 0, amount: 0, value: 0, margin: 0 };
  const refChanged = data && refValue !== String(data.reference_value_per_enseigne ?? "");

  return (
    <>
      {/* Barre d'outils : navigation du mois + valeur de référence */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="billing-month-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] px-2 text-center text-sm font-bold capitalize" data-testid="billing-month-label">{frMonth(month)}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="billing-month-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <div className="flex items-end gap-2 rounded-lg border border-[var(--border-soft)] bg-white/80 p-2">
          <div>
            <label className="block text-[10px] font-semibold uppercase text-muted-ink">Valeur de référence / enseigne</label>
            <div className="mt-1 flex items-center gap-2">
              <div className="relative">
                <Coins className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />
                <Input type="number" step="0.01" min="0" value={refValue} onChange={(e) => setRefValue(e.target.value)}
                  className="h-9 w-32 pl-7" data-testid="billing-ref-input" />
              </div>
              <Button size="sm" onClick={saveRef} disabled={savingRef || !refChanged}
                className="h-9 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="billing-ref-save">
                {savingRef ? <Loader2Icon /> : <><Save className="mr-1.5 h-3.5 w-3.5" />Enregistrer</>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-[var(--radius)]" />)}</div>
          <Skeleton className="h-72 w-full rounded-[var(--radius)]" />
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="billing-summary">
            <SummaryTile icon={Store} label="Enseignes trouvées" value={totals.enseignes} />
            <SummaryTile icon={Wallet} label="Coût total CDP" value={eur(totals.amount)} />
            <SummaryTile icon={Receipt} label="Valeur générée" value={eur(totals.value)} accent />
            <MarginTile value={totals.margin} />
          </div>

          <div className="mb-3 flex items-center gap-2 rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2 text-[11px] text-muted-ink" data-testid="billing-info">
            <HelpCircle className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
            <span>
              Une enseigne (= 1 spot, quel que soit le nombre de dates) est comptée une seule fois pour le CDP. En mode <strong>« À l'enseigne »</strong>, chaque magasin booké est compté dès qu'il est planifié (tout statut sauf annulé, dates à venir incluses), rattaché au <strong>mois de sa 1re date de passage</strong> et affiché ce jour-là avec toutes ses dates. En mode <strong>horaire / forfait</strong>, chaque magasin booké (au crayon inclus) est rattaché au <strong>jour où il a été booké</strong> (et non aux dates de passage) et impacte la marge de ce jour ; les dates de campagne sont indiquées en information. L'enseigne n'est comptée qu'une fois (le mois où elle est bookée).
              Ouvrez <strong>Jour par jour</strong> pour voir le détail quotidien (heures modifiables, spots décrochés, coût &amp; marge) et retirer un spot de test. Réglez le mode de facturation, les tarifs, la date d'entrée et les absences via l'icône <span className="inline-flex"><SlidersHorizontal className="mx-0.5 inline h-3 w-3" /></span>. Marge = (enseignes × {eur(data?.reference_value_per_enseigne)}) − coût du CDP.
            </span>
          </div>

          <div className={`${card} overflow-hidden`} data-testid="billing-table">
            <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-2.5">
              <h3 className="flex items-center gap-1.5 text-sm font-bold"><Wallet className="h-4 w-4 text-[var(--shariti-orange)]" />Facturation par CDP</h3>
              <span className="text-[11px] capitalize text-muted-ink">{frMonth(month)} · {data?.days_in_month} jours</span>
            </div>
            {rows.length === 0 ? (
              <div className="grid place-items-center gap-2 py-14 text-center" data-testid="billing-empty">
                <Wallet className="h-8 w-8 text-[var(--border-soft)]" />
                <p className="text-sm font-semibold text-muted-ink">Aucun CDP actif à facturer</p>
                <p className="text-xs text-muted-ink">Ajoutez des CDP dans l'Administration pour voir leur facturation.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm" data-testid="billing-rows">
                  <thead>
                    <tr className="border-b border-[var(--border-soft)] text-[10px] uppercase tracking-wide text-muted-ink">
                      <th className="px-4 py-2 text-left font-semibold">CDP</th>
                      <th className="px-3 py-2 text-left font-semibold">Contrat</th>
                      <th className="px-3 py-2 text-right font-semibold">Base</th>
                      <th className="px-3 py-2 text-right font-semibold">Enseignes</th>
                      <th className="px-3 py-2 text-right font-semibold">Coût</th>
                      <th className="px-3 py-2 text-right font-semibold">Valeur</th>
                      <th className="px-3 py-2 text-right font-semibold">Marge</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border-soft)]">
                    {rows.map((r) => {
                      const bl = BILLING_LABEL[r.billing_type] || BILLING_LABEL.hourly;
                      const positive = r.margin >= 0;
                      return (
                        <tr key={r.cdp_id} className={`transition-colors hover:bg-[var(--surface-2)] ${positive ? "" : "bg-[rgba(214,69,69,0.04)]"}`} data-testid={`billing-row-${r.cdp_id}`}>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: r.color || "#94A3B8" }} />
                              <span className="break-words font-bold">{r.cdp_name || "—"}</span>
                              {!positive && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[rgba(214,69,69,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#9B2226]" data-testid={`billing-alert-${r.cdp_id}`}>
                                  <AlertTriangle className="h-3 w-3" />Non rentable
                                </span>
                              )}
                            </div>
                            {(r.contract_start || r.contract_end || r.absences_count > 0) && (
                              <div className="mt-0.5 pl-5 text-[10px] text-muted-ink" data-testid={`billing-contract-info-${r.cdp_id}`}>
                                {r.contract_start ? `Entrée ${frShort(r.contract_start)}` : ""}
                                {r.contract_start && (r.contract_end || r.absences_count > 0) ? " · " : ""}
                                {r.contract_end ? `Sortie ${frShort(r.contract_end)}` : ""}
                                {r.contract_end && r.absences_count > 0 ? " · " : ""}
                                {r.absences_count > 0 ? `${r.absences_count} absence${r.absences_count > 1 ? "s" : ""}` : ""}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${bl.tone}`}>
                              <bl.icon className="h-3 w-3" />{bl.label}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-muted-ink">
                            {r.billing_type === "per_enseigne" ? (
                              <span>{eur(r.rate_per_enseigne)} / ens.</span>
                            ) : r.billing_type === "forfait" ? (
                              <div className="leading-tight">
                                <div>{eur(r.monthly_rate)} <span className="text-[10px]">forfait</span></div>
                                {r.prorata < 1 && (
                                  <div className="text-[10px] font-semibold text-[var(--shariti-orange)]" data-testid={`billing-prorata-${r.cdp_id}`}>
                                    prorata {Math.round((r.prorata || 0) * 100)}% · {r.worked_days}/{r.worked_days_full} j
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="leading-tight">
                                <div>{r.hours ?? 0} h · {eur(r.hourly_rate)}/h</div>
                                <div className="text-[10px]">{r.worked_days}/{r.worked_days_full} j{r.absences_count > 0 ? ` · ${r.absences_count} abs.` : ""}</div>
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right text-base font-extrabold tabular-nums">{r.enseignes_count}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{eur(r.amount)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{eur(r.value)}</td>
                          <td className="px-3 py-2.5 text-right">
                            <span className="inline-flex items-center gap-1 font-extrabold tabular-nums"
                              style={{ color: positive ? "#0F5A2F" : "#9B2226" }}>
                              {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                              {eur(r.margin)}
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-ink hover:text-[var(--shariti-orange)]"
                                onClick={() => setSettingsEdit(r)} title="Réglages : mode de facturation, tarifs, contrat & absences" data-testid={`billing-settings-${r.cdp_id}`}>
                                <SlidersHorizontal className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="outline" size="sm" className="h-7 gap-1 text-xs"
                                onClick={() => setDrill(r)} data-testid={`billing-detail-${r.cdp_id}`}>
                                <CalendarRange className="h-3 w-3" />Jour par jour
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-[var(--border-soft)] bg-[var(--surface-2)] font-extrabold" data-testid="billing-total-row">
                      <td className="px-4 py-2.5" colSpan={3}>Total</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{totals.enseignes}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{eur(totals.amount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{eur(totals.value)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: totals.margin >= 0 ? "#0F5A2F" : "#9B2226" }} data-testid="billing-total-margin">{eur(totals.margin)}</td>
                      <td className="px-3 py-2.5" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Panneau JOUR PAR JOUR : heures éditables, spots décrochés, coût & marge */}
      <DailySheet row={drill} month={month} refValue={data?.reference_value_per_enseigne}
        onClose={() => setDrill(null)} onChanged={load} />

      {/* Réglages CDP : mode de facturation, tarifs, rythme, contrat & absences */}
      <CdpSettingsSheet row={settingsEdit} onClose={() => setSettingsEdit(null)} onSaved={load} />
    </>
  );
}

/* ---------- Réglages CDP unifiés : mode de facturation, tarifs, contrat ------- */
const BILLING_OPTIONS = [
  { v: "hourly", label: "À l'heure", icon: Clock, hint: "Payé au taux horaire" },
  { v: "per_enseigne", label: "Au spot", icon: Store, hint: "Payé par enseigne trouvée" },
  { v: "forfait", label: "Forfait", icon: Coins, hint: "Montant fixe mensuel (prorata)" },
];

function CdpSettingsSheet({ row, onClose, onSaved }) {
  const [billingType, setBillingType] = useState("hourly");
  const [hourlyRate, setHourlyRate] = useState("");
  const [ratePerEnseigne, setRatePerEnseigne] = useState("");
  const [monthlyRate, setMonthlyRate] = useState("");
  const [daysPerWeek, setDaysPerWeek] = useState("");
  const [workDays, setWorkDays] = useState([0, 1, 2, 3, 4]);
  const toggleWorkDay = (i) => setWorkDays((w) =>
    w.includes(i) ? w.filter((x) => x !== i) : [...w, i].sort((a, b) => a - b));
  const [hoursPerDay, setHoursPerDay] = useState("");
  const [contractStart, setContractStart] = useState("");
  const [contractEnd, setContractEnd] = useState("");
  const [absences, setAbsences] = useState([]);
  const [newAbs, setNewAbs] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (row) {
      setBillingType(row.billing_type || "hourly");
      setHourlyRate(String(row.hourly_rate ?? ""));
      setRatePerEnseigne(String(row.rate_per_enseigne ?? ""));
      setMonthlyRate(String(row.monthly_rate ?? ""));
      setDaysPerWeek(String(row.days_per_week ?? ""));
      setWorkDays(Array.isArray(row.work_days) && row.work_days.length
        ? [...row.work_days].sort((a, b) => a - b)
        : (row.days_per_week != null ? Array.from({ length: row.days_per_week }, (_, i) => i) : [0, 1, 2, 3, 4]));
      setHoursPerDay(String(row.hours_per_day ?? ""));
      setContractStart(row.contract_start || "");
      setContractEnd(row.contract_end || "");
      setAbsences(row.absences || []);
      setNewAbs("");
    }
  }, [row]);

  const addAbs = () => {
    if (!newAbs) return;
    setAbsences((a) => (a.includes(newAbs) ? a : [...a, newAbs].sort()));
    setNewAbs("");
  };
  const removeAbs = (d) => setAbsences((a) => a.filter((x) => x !== d));

  const save = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await api.put(`/performance/cdp-billing/cdp/${row.cdp_id}/rhythm`, {
        billing_type: billingType,
        hourly_rate: Number(hourlyRate) || 0,
        rate_per_enseigne: Number(ratePerEnseigne) || 0,
        monthly_rate: Number(monthlyRate) || 0,
        days_per_week: workDays.length,
        work_days: workDays,
        hours_per_day: Number(hoursPerDay) || 0,
      });
      await api.put(`/performance/cdp-billing/cdp/${row.cdp_id}/contract`, {
        contract_start: contractStart || null,
        contract_end: contractEnd || null,
        absences,
      });
      toast.success("Réglages du CDP enregistrés");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-[480px]" data-testid="settings-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            {row && <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: row.color || "#94A3B8" }} />}
            Réglages — {row?.cdp_name}
          </SheetTitle>
          <SheetDescription>
            Mode de facturation, tarifs, rythme de travail, date d'entrée et absences. Tout est personnalisable par CDP.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-6">
          {/* Mode de facturation */}
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">Mode de facturation</label>
            <div className="grid grid-cols-3 gap-1.5" data-testid="settings-billing-type">
              {BILLING_OPTIONS.map((o) => {
                const active = billingType === o.v;
                return (
                  <button key={o.v} onClick={() => setBillingType(o.v)} data-testid={`settings-billing-${o.v}`}
                    className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center transition-colors ${active ? "border-[var(--shariti-orange)] bg-[var(--surface-2)] text-[var(--shariti-orange)]" : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                    <o.icon className="h-4 w-4" />
                    <span className="text-xs font-bold">{o.label}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-muted-ink">{BILLING_OPTIONS.find((o) => o.v === billingType)?.hint}</p>
          </div>

          {/* Tarifs selon le mode */}
          <div className="grid grid-cols-2 gap-3">
            {billingType === "hourly" && (
              <>
                <Field label="Taux horaire (€/h)" testId="settings-hourly-rate" value={hourlyRate} onChange={setHourlyRate} icon={Coins} />
                <Field label="Heures / jour" testId="settings-hours-per-day" value={hoursPerDay} onChange={setHoursPerDay} icon={Clock} />
              </>
            )}
            {billingType === "per_enseigne" && (
              <Field label="Tarif / spot (€)" testId="settings-rate-enseigne" value={ratePerEnseigne} onChange={setRatePerEnseigne} icon={Store} />
            )}
            {billingType === "forfait" && (
              <Field label="Forfait mensuel (€/mois)" testId="settings-monthly-rate" value={monthlyRate} onChange={setMonthlyRate} icon={Coins} />
            )}
            {billingType !== "hourly" && (
              <Field label="Heures / jour (indicatif)" testId="settings-hours-per-day-2" value={hoursPerDay} onChange={setHoursPerDay} icon={Clock} />
            )}
          </div>
          {/* Jours travaillés EXACTS de la semaine (ex. mardi→samedi). */}
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">Jours travaillés</label>
            <div className="flex flex-wrap gap-1.5" data-testid="settings-workdays">
              {[["Lun", 0], ["Mar", 1], ["Mer", 2], ["Jeu", 3], ["Ven", 4], ["Sam", 5], ["Dim", 6]].map(([lbl, i]) => {
                const on = workDays.includes(i);
                return (
                  <button key={i} type="button" onClick={() => toggleWorkDay(i)} aria-pressed={on}
                    data-testid={`settings-workday-${i}`}
                    className={`h-8 min-w-[42px] rounded-lg border px-2 text-[12px] font-semibold transition-colors ${on
                      ? "border-[var(--shariti-orange)] bg-[var(--shariti-orange)] text-white"
                      : "border-[var(--border-soft)] bg-white text-muted-ink hover:bg-[var(--surface-2)]"}`}>
                    {lbl}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-[11px] text-muted-ink" data-testid="settings-days-hint">
              <b>{workDays.length} jour(s) / semaine</b> — cochez les jours réellement travaillés (ex. mardi→samedi). Détermine les jours facturés et le prorata du forfait.
            </p>
          </div>

          {/* Date d'entrée */}
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">Date d'entrée</label>
            <div className="flex items-center gap-2">
              <Input type="date" value={contractStart} onChange={(e) => setContractStart(e.target.value)}
                className="h-9" data-testid="contract-start-input" />
              {contractStart && (
                <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-ink" onClick={() => setContractStart("")}
                  title="Effacer la date d'entrée" data-testid="contract-start-clear"><X className="h-4 w-4" /></Button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-ink">Les jours avant cette date ne sont pas facturés — corrige le prorata d'un démarrage en cours de mois (ex. Octavi).</p>
          </div>

          {/* Date de sortie / fin de contrat */}
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">Date de sortie (fin de contrat)</label>
            <div className="flex items-center gap-2">
              <Input type="date" value={contractEnd} min={contractStart || undefined}
                onChange={(e) => setContractEnd(e.target.value)}
                className="h-9" data-testid="contract-end-input" />
              {contractEnd && (
                <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-ink" onClick={() => setContractEnd("")}
                  title="Effacer la date de sortie" data-testid="contract-end-clear"><X className="h-4 w-4" /></Button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-muted-ink">Aucun coût n'est facturé après cette date : le CDP sorti des effectifs disparaît des mois suivants (son historique reste intact). Renseignée automatiquement à la désactivation du compte, ajustable ici.</p>
          </div>

          {/* Absences */}
          <div>
            <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">Jours d'absence</label>
            <div className="flex items-center gap-2">
              <Input type="date" value={newAbs} onChange={(e) => setNewAbs(e.target.value)}
                className="h-9" data-testid="contract-abs-input" />
              <Button size="sm" onClick={addAbs} disabled={!newAbs}
                className="h-9 shrink-0 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="contract-abs-add">
                <Plus className="mr-1 h-3.5 w-3.5" />Ajouter
              </Button>
            </div>
            {absences.length === 0 ? (
              <p className="mt-2 text-[11px] text-muted-ink" data-testid="contract-abs-empty">Aucune absence enregistrée.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="contract-abs-list">
                {absences.map((d) => (
                  <span key={d} className="inline-flex items-center gap-1 rounded-full border border-[var(--border-soft)] bg-[var(--surface-2)] py-1 pl-2.5 pr-1 text-xs font-semibold" data-testid={`contract-abs-chip-${d}`}>
                    {frShort(d)}
                    <button onClick={() => removeAbs(d)} className="grid h-4 w-4 place-items-center rounded-full text-muted-ink hover:bg-black/10 hover:text-ink" title="Retirer" data-testid={`contract-abs-remove-${d}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="mt-1 text-[11px] text-muted-ink">Chaque absence retire un jour travaillé (horaire) et réduit le prorata (forfait).</p>
          </div>

          <div className="flex justify-end gap-2 border-t border-[var(--border-soft)] pt-4">
            <Button variant="outline" onClick={onClose} data-testid="settings-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="settings-save">
              {saving ? <Loader2Icon /> : <><Save className="mr-1.5 h-3.5 w-3.5" />Enregistrer</>}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const Field = ({ label, value, onChange, icon: Icon, testId }) => (
  <div>
    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">{label}</label>
    <div className="relative">
      {Icon && <Icon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-ink" />}
      <Input type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 pl-7" data-testid={testId} />
    </div>
  </div>
);

/* ------- Dialogue : aperçu + envoi de la facturation Excel au CDP ------------ */
/* Aperçu AVANT envoi · destinataire modifiable (défaut = e-mail du CDP) ·       */
/* BCC fixe echgroupsas@gmail.com · fichier SANS marges. Chaque e-mail ne        */
/* concerne QU'UN seul CDP (ses données uniquement).                             */
const BILLING_BCC = "echgroupsas@gmail.com";
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function BillingEmailDialog({ open, onClose, cdp, month }) {
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (open) setTo(cdp?.cdp_email || "");
  }, [open, cdp]);

  if (!cdp) return null;
  const isHourly = cdp.billing_type === "hourly" || cdp.billing_type === "forfait";

  // Lignes d'aperçu : mêmes règles d'affichage que le fichier Excel (jours utiles),
  // et surtout AUCUNE marge ni valeur interne.
  const rows = (cdp.days || [])
    .map((d) => {
      const counted = (d.spots || []).filter((s) => s.counted);
      const label = counted
        .map((s) => (s.ville ? `${s.enseigne} (${s.ville})` : s.enseigne))
        .join(", ");
      return {
        date: d.date, weekday: d.weekday, hours: d.hours || 0,
        nb: counted.length, cost: d.cost || 0, label,
        abs: d.is_absence, training: d.is_training, working: d.is_working_day,
      };
    })
    .filter((r) => r.nb > 0 || r.cost > 0 || r.label || r.working);

  const totalHours = Math.round(rows.reduce((a, r) => a + r.hours, 0) * 100) / 100;
  const totalNb = rows.reduce((a, r) => a + r.nb, 0);
  const totalCost = Math.round(rows.reduce((a, r) => a + r.cost, 0) * 100) / 100;

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/performance/cdp-billing/daily/export", {
        params: { cdp_id: cdp.cdp_id, month }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Facturation_${(cdp.cdp_name || "CDP").replace(/\s+/g, "_")}_${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloading(false);
    }
  };

  const send = async () => {
    if (!EMAIL_RE.test((to || "").trim())) {
      toast.error("Adresse e-mail du destinataire invalide");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/performance/cdp-billing/daily/email", {
        cdp_id: cdp.cdp_id, month, to: to.trim(),
      });
      toast.success(`Facturation envoyée à ${data.to} (copie cachée : ${data.bcc})`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi de l'e-mail");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden" data-testid="billing-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[var(--shariti-orange)]" />
            Envoyer la facturation par e-mail
          </DialogTitle>
          <p className="text-sm text-muted-ink">
            <span className="font-semibold text-ink">{cdp.cdp_name}</span> · <span className="capitalize">{frMonth(month)}</span>
            {" "}— détail jour par jour, <span className="font-semibold">sans les marges</span>.
          </p>
        </DialogHeader>

        {/* Aperçu AVANT envoi */}
        <div className="min-h-0 flex-1 overflow-y-auto rounded-[var(--radius)] border border-[var(--border-soft)]" data-testid="billing-email-preview">
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-[var(--shariti-orange)] text-left text-[10px] uppercase tracking-wide text-white">
                <th className="px-2.5 py-2 font-semibold">Date</th>
                <th className="px-2.5 py-2 font-semibold">Jour</th>
                {isHourly && <th className="px-2.5 py-2 text-right font-semibold">Heures</th>}
                <th className="px-2.5 py-2 font-semibold">Enseignes</th>
                <th className="px-2.5 py-2 text-right font-semibold">Nb</th>
                <th className="px-2.5 py-2 text-right font-semibold">Montant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-soft)]">
              {rows.length === 0 ? (
                <tr><td colSpan={isHourly ? 6 : 5} className="px-3 py-8 text-center text-sm text-muted-ink">Aucune ligne à facturer ce mois-ci.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.date} className="align-top hover:bg-[var(--surface-2)]/60" data-testid={`billing-preview-row-${r.date}`}>
                  <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">{frShort(r.date)}</td>
                  <td className="px-2.5 py-1.5">
                    {r.abs && <span className="mr-1 rounded bg-[rgba(185,28,28,0.1)] px-1 text-[10px] font-bold text-[#9B2226]">absence</span>}
                    {r.training && <span className="mr-1 rounded bg-[rgba(47,111,237,0.1)] px-1 text-[10px] font-bold text-[#1D4ED8]">formation</span>}
                  </td>
                  {isHourly && <td className="px-2.5 py-1.5 text-right tabular-nums">{r.hours ? `${r.hours} h` : "—"}</td>}
                  <td className="px-2.5 py-1.5 text-muted-ink">{r.label || "—"}</td>
                  <td className="px-2.5 py-1.5 text-right font-bold tabular-nums">{r.nb || "—"}</td>
                  <td className="px-2.5 py-1.5 text-right font-bold tabular-nums text-ink">{eur(r.cost)}</td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="sticky bottom-0">
                <tr className="bg-[var(--surface-2)] font-bold text-ink">
                  <td className="px-2.5 py-2" colSpan={2}>TOTAL</td>
                  {isHourly && <td className="px-2.5 py-2 text-right tabular-nums">{totalHours} h</td>}
                  <td className="px-2.5 py-2" />
                  <td className="px-2.5 py-2 text-right tabular-nums">{totalNb}</td>
                  <td className="px-2.5 py-2 text-right tabular-nums text-[var(--shariti-orange)]">{eur(totalCost)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* Destinataire + BCC */}
        <div className="space-y-2">
          <div>
            <label htmlFor="billing-email-to" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-muted-ink">
              Destinataire (modifiable)
            </label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-ink" />
              <Input id="billing-email-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
                placeholder="prenom.nom@exemple.com" className="h-9 pl-8" data-testid="billing-email-to" />
            </div>
          </div>
          <p className="flex items-center gap-1.5 rounded-lg bg-[var(--surface-2)] px-3 py-1.5 text-[11px] font-medium text-muted-ink" data-testid="billing-email-bcc-note">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
            Copie cachée automatique à <span className="font-semibold text-ink">{BILLING_BCC}</span>. Seules les données de <span className="font-semibold text-ink">{cdp.cdp_name}</span> sont envoyées.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={download} disabled={downloading} className="gap-1.5" data-testid="billing-email-download">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Télécharger l'Excel
          </Button>
          <Button onClick={send} disabled={sending || rows.length === 0}
            className="gap-1.5 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" data-testid="billing-email-send">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Envoyer la facturation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------ Panneau JOUR PAR JOUR d'un CDP ---------------------- */
function DailySheet({ row, month, onClose, onChanged }) {
  const [cdp, setCdp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("day"); // "day" | "week"
  const [pendingExclude, setPendingExclude] = useState(null); // spot en attente de confirmation
  const [excluding, setExcluding] = useState(false);
  const [addDay, setAddDay] = useState(null);   // date préremplie du dialogue « Ajouter un jour »
  const [bulkBusy, setBulkBusy] = useState("");  // action groupée en cours
  const [emailOpen, setEmailOpen] = useState(false);  // dialogue d'envoi de la facturation

  const load = useCallback(async () => {
    if (!row) return;
    setLoading(true);
    try {
      const { data } = await api.get("/performance/cdp-billing/daily", { params: { cdp_id: row.cdp_id, month } });
      setCdp(data.cdp);
    } catch {
      toast.error("Impossible de charger le détail jour par jour");
    } finally { setLoading(false); }
  }, [row, month]);

  useEffect(() => {
    if (row) { setMode("day"); load(); } else { setCdp(null); }
  }, [row, load]);

  const refresh = async () => { await load(); onChanged && onChanged(); };

  const confirmExclude = async () => {
    if (!pendingExclude) return;
    setExcluding(true);
    try {
      await api.post(`/performance/cdp-billing/spot/${pendingExclude.spot_id}/exclude`, { excluded: true });
      toast.success(`« ${pendingExclude.enseigne || "Spot"} » retiré de la facturation`);
      setPendingExclude(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du retrait");
    } finally { setExcluding(false); }
  };

  const removeManual = async (date, mid) => {
    try {
      await api.put(`/performance/cdp-billing/cdp/${row.cdp_id}/day-entry`, { date, remove_enseigne_id: mid });
      toast.success("Enseigne retirée");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Retrait impossible"); }
  };

  const runBulk = async (action) => {
    setBulkBusy(action);
    try {
      const { data } = await api.post(`/performance/cdp-billing/cdp/${row.cdp_id}/bulk-hours`, { month, action });
      toast.success(action === "zero_empty"
        ? `${data.affected} jour(s) vide(s) marqué(s) « non travaillé » (0 h)`
        : `${data.affected} jour(s) réinitialisé(s) au rythme théorique`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setBulkBusy(""); }
  };

  const bl = cdp ? (BILLING_LABEL[cdp.billing_type] || BILLING_LABEL.hourly) : null;
  const days = cdp?.days || [];
  const weeks = (() => {
    const map = {};
    for (const d of days) {
      const wk = mondayOf(d.date);
      const w = map[wk] || (map[wk] = { monday: wk, hours: 0, spots: 0, cost: 0, value: 0, margin: 0 });
      w.hours += d.hours; w.spots += d.spots_count; w.cost += d.cost; w.value += d.value; w.margin += d.margin;
    }
    return Object.values(map).sort((a, b) => a.monday.localeCompare(b.monday));
  })();

  return (
    <Sheet open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col overflow-hidden p-0 sm:max-w-[760px]" data-testid="daily-sheet">
        <SheetHeader className="border-b border-[var(--border-soft)] p-4 text-left">
          <SheetTitle className="flex items-center gap-2">
            {row && <span className="h-3 w-3 rounded-full border border-black/10" style={{ background: row.color || "#94A3B8" }} />}
            {row?.cdp_name} — Jour par jour
            {bl && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${bl.tone}`}><bl.icon className="h-3 w-3" />{bl.label}</span>}
          </SheetTitle>
          <SheetDescription className="capitalize">{frMonth(month)}</SheetDescription>
        </SheetHeader>

        {loading || !cdp ? (
          <div className="space-y-3 p-4">
            <div className="grid grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-[var(--radius)]" />)}</div>
            <Skeleton className="h-80 w-full rounded-[var(--radius)]" />
          </div>
        ) : (
          <>
            {/* Synthèse du mois — ARRÊTÉE À AUJOURD'HUI (rentabilité à date) */}
            <div className="grid grid-cols-2 gap-2 p-4 pb-1 sm:grid-cols-4" data-testid="daily-sheet-summary">
              {cdp.billing_type === "hourly" ? (
                <MiniStat icon={Clock} label="Heures à date" value={cdp.hours} />
              ) : cdp.billing_type === "forfait" ? (
                <MiniStat icon={Coins} label="Forfait / mois" value={eur(cdp.monthly_rate)} />
              ) : (
                <MiniStat icon={Coins} label="Tarif / ens." value={eur(cdp.rate_per_enseigne)} />
              )}
              <MiniStat icon={Store} label={cdp.hours_relevant ? "Spots" : "Enseignes"} value={cdp.enseignes_count} />
              <MiniStat icon={Wallet} label="Coût à date" value={eur(cdp.amount)} />
              <MiniStat icon={cdp.margin >= 0 ? TrendingUp : TrendingDown} label="Marge à date"
                value={eur(cdp.margin)} tone={cdp.margin >= 0 ? "ok" : "bad"} />
            </div>

            {/* Rappel : en horaire/forfait le calcul s'arrête à aujourd'hui. En « à
                l'enseigne », chaque magasin planifié compte, dates à venir incluses. */}
            {cdp.billing_type !== "per_enseigne" && (
              <div className="mx-4 mb-1 flex items-start gap-1.5 rounded-lg bg-[rgba(47,111,237,0.06)] px-3 py-1.5 text-[11px] font-medium text-muted-ink" data-testid="daily-todate-note">
                <CalendarClock className="mt-0.5 h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                <span>
                  <b className="text-ink">Jour par jour</b> — chaque emplacement booké est rattaché au <b>jour où il a été booké</b> (au crayon inclus) et impacte la marge de ce jour ; les <b>dates de campagne (passage)</b> sont indiquées en information. Une enseigne bookée un mois précédent reste rattachée à son <b>mois de booking</b> (non recomptée : 1 enseigne = 1 valeur). Le coût horaire/forfait est calculé <b>à date</b> (jours à venir non facturés, modifiable au crayon).
                </span>
              </div>
            )}

            {cdp.billing_type === "per_enseigne" && (
              <div className="mx-4 -mt-1 mb-1 flex items-start gap-1.5 rounded-lg bg-[rgba(238,114,46,0.08)] px-3 py-1.5 text-[11px] font-medium text-[#8A3A10]" data-testid="daily-per-enseigne-note">
                <Store className="mt-0.5 h-3 w-3 shrink-0" />
                Payé à l'enseigne : pas d'heures par défaut. Coût = enseignes comptées × {eur(cdp.rate_per_enseigne)}. Chaque magasin booké compte dès qu'il est planifié (dates à venir incluses) et s'affiche sur sa 1re date de passage avec toutes ses dates. Une enseigne n'est comptée qu'une seule fois, le mois de sa 1re date de passage.
              </div>
            )}

            {cdp.billing_type === "forfait" && (
              <div className="mx-4 mb-2 rounded-lg bg-[rgba(233,184,0,0.15)] px-3 py-2 text-[11px] font-semibold text-[#5A4200]" data-testid="daily-prorata-note">
                Forfait {eur(cdp.monthly_rate)}/mois · prorata <b>à date</b> {Math.round((cdp.prorata || 0) * 100)}% ({cdp.worked_days}/{cdp.worked_days_full} jours ouvrés écoulés) → {eur(cdp.amount)} · {eur(cdp.forfait_per_day)}/jour ouvré
              </div>
            )}

            {/* Sélecteur Jour / Semaine + actions */}
            <div className="flex flex-wrap items-center justify-between gap-2 px-4">
              <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white/80 p-1" data-testid="daily-mode-toggle">
                <button onClick={() => setMode("day")} data-testid="daily-mode-day"
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${mode === "day" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>Par jour</button>
                <button onClick={() => setMode("week")} data-testid="daily-mode-week"
                  className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${mode === "week" ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>Par semaine</button>
              </div>
              <div className="flex items-center gap-2">
                {cdp?.billing_type === "per_enseigne" && (
                  <Button size="sm" variant="outline" className="h-8 gap-1.5 border-[var(--shariti-orange)]/40 text-[var(--shariti-orange)] hover:bg-[var(--surface-2)]"
                    onClick={() => setEmailOpen(true)} data-testid="daily-email-btn" title="Aperçu et envoi de la facturation par e-mail (Excel, sans marges)">
                    <Mail className="h-3.5 w-3.5" />E-mail facturation
                  </Button>
                )}
                <Button size="sm" className="h-8 gap-1.5 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]"
                  onClick={() => setAddDay(defaultAddDate(month))} data-testid="daily-add-day-btn">
                  <CalendarPlus className="h-3.5 w-3.5" />Ajouter un jour
                </Button>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="icon" variant="outline" className="h-8 w-8" title="Actions groupées" data-testid="daily-bulk-btn">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72 p-1.5" data-testid="daily-bulk-menu">
                    <p className="px-2 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-ink">Actions groupées du mois</p>
                    {cdp.hours_relevant && (
                      <button onClick={() => runBulk("zero_empty")} disabled={!!bulkBusy}
                        className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-[var(--surface-2)] disabled:opacity-50" data-testid="daily-bulk-zero">
                        {bulkBusy === "zero_empty" ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" /> : <Eraser className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />}
                        <span><span className="font-semibold">Jours vides → non travaillés</span><span className="block text-[11px] text-muted-ink">Met à 0 h les jours ouvrés sans enseigne (gain de temps).</span></span>
                      </button>
                    )}
                    <button onClick={() => runBulk("clear_all")} disabled={!!bulkBusy}
                      className="flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left text-sm hover:bg-[var(--surface-2)] disabled:opacity-50" data-testid="daily-bulk-clear">
                      {bulkBusy === "clear_all" ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin" /> : <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-muted-ink" />}
                      <span><span className="font-semibold">Réinitialiser les heures</span><span className="block text-[11px] text-muted-ink">Retire tous les ajustements d'heures (retour au rythme).</span></span>
                    </button>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="mt-2 flex-1 overflow-y-auto px-4 pb-6">
              {mode === "week" ? (
                <div className="overflow-hidden rounded-[var(--radius)] border border-[var(--border-soft)]" data-testid="daily-week-list">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-2)] text-[10px] uppercase tracking-wide text-muted-ink">
                        <th className="px-3 py-2 text-left font-semibold">Semaine du</th>
                        <th className="px-3 py-2 text-right font-semibold">Heures</th>
                        <th className="px-3 py-2 text-right font-semibold">Spots</th>
                        <th className="px-3 py-2 text-right font-semibold">Coût</th>
                        <th className="px-3 py-2 text-right font-semibold">Marge</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-soft)]">
                      {weeks.map((w) => (
                        <tr key={w.monday} data-testid={`daily-week-${w.monday}`}>
                          <td className="px-3 py-2.5 font-semibold capitalize">{frShort(w.monday)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{Math.round(w.hours * 100) / 100} h</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-bold">{w.spots}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{eur(Math.round(w.cost * 100) / 100)}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-extrabold" style={{ color: w.margin >= 0 ? "#0F5A2F" : "#9B2226" }}>{eur(Math.round(w.margin * 100) / 100)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="space-y-1.5" data-testid="daily-day-list">
                  {days.length === 0 ? (
                    <p className="py-10 text-center text-sm text-muted-ink">Aucun jour travaillé ni spot ce mois-ci.</p>
                  ) : days.map((d) => (
                    <DayRow key={d.date} day={d} cdpId={cdp.cdp_id} billingType={cdp.billing_type}
                      hoursRelevant={cdp.hours_relevant} onSaved={refresh}
                      onAskExclude={setPendingExclude} onAddDay={setAddDay}
                      onRemoveManual={removeManual} />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>

      {/* Confirmation retrait d'un spot de la facturation */}
      <AlertDialog open={!!pendingExclude} onOpenChange={(o) => { if (!o) setPendingExclude(null); }}>
        <AlertDialogContent data-testid="daily-exclude-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce spot de la facturation ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {pendingExclude?.enseigne || "Spot"} » ne sera plus compté dans la rentabilité (spot de test, erroné, ou magasin rétracté). Cette action est réversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="daily-exclude-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); confirmExclude(); }} disabled={excluding}
              className="bg-[#B91C1C] text-white hover:bg-[#9B2226]" data-testid="daily-exclude-confirm">
              {excluding ? "Retrait…" : "Retirer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Insertion / complément d'un jour (jour manquant, formation, enseignes trouvées) */}
      {row && (
        <AddDayDialog open={!!addDay} date={addDay} cdpId={row.cdp_id}
          hoursRelevant={cdp?.hours_relevant !== false}
          defaultHours={cdp?.hours_per_day ?? 4}
          onClose={() => setAddDay(null)}
          onSaved={async () => { setAddDay(null); await refresh(); }} />
      )}

      {/* Aperçu + envoi de la facturation Excel au CDP (sans marges) */}
      <BillingEmailDialog open={emailOpen} onClose={() => setEmailOpen(false)} cdp={cdp} month={month} />
    </Sheet>
  );
}

// Date par défaut du dialogue « Ajouter un jour » : aujourd'hui si dans le mois
// affiché, sinon le 1er du mois.
function defaultAddDate(month) {
  const today = todayIso();
  return today.slice(0, 7) === month ? today : `${month}-01`;
}

function AddDayDialog({ open, date, cdpId, hoursRelevant, defaultHours, onClose, onSaved }) {
  const [d, setD] = useState(date || "");
  const [isTraining, setIsTraining] = useState(false);
  const [hours, setHours] = useState(String(defaultHours ?? 4));
  const [ens, setEns] = useState([]);          // enseignes à ajouter [{name, ville}]
  const [name, setName] = useState("");
  const [ville, setVille] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setD(date || "");
      setIsTraining(false);
      setHours(String(defaultHours ?? 4));
      setEns([]); setName(""); setVille("");
    }
  }, [open, date, defaultHours]);

  const addEns = () => {
    const n = name.trim();
    if (!n) return;
    setEns((l) => [...l, { name: n, ville: ville.trim() }]);
    setName(""); setVille("");
  };

  const submit = async () => {
    if (!d) { toast.error("Choisissez une date"); return; }
    const body = { date: d, is_training: isTraining, add_enseignes: ens };
    if (hoursRelevant && hours !== "" && !isTraining) {
      const h = Number(hours);
      if (!(h >= 0 && h <= 24)) { toast.error("Heures entre 0 et 24"); return; }
      body.hours = h;
    }
    if (hoursRelevant && isTraining && hours !== "") {
      const h = Number(hours);
      if (h >= 0 && h <= 24) body.hours = h;  // formation payée : on garde les heures
    }
    if (!ens.length && !isTraining && !(hoursRelevant && body.hours != null)) {
      toast.error("Ajoutez au moins une enseigne, des heures ou marquez « formation »."); return;
    }
    setSaving(true);
    try {
      await api.put(`/performance/cdp-billing/cdp/${cdpId}/day-entry`, body);
      toast.success("Jour enregistré");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="grid max-h-[90vh] w-[calc(100vw-2rem)] max-w-md gap-3 overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="add-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-[var(--shariti-orange)]" />Ajouter / compléter un jour</DialogTitle>
        </DialogHeader>

        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Date</label>
          <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="mt-1" data-testid="add-day-date" />
          <p className="mt-1 text-[11px] text-muted-ink">Vous pouvez saisir un jour hors rythme (ex. un samedi travaillé).</p>
        </div>

        <label className="flex items-center justify-between rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] px-3 py-2" data-testid="add-day-training-row">
          <span className="flex items-center gap-2 text-sm font-semibold"><GraduationCap className="h-4 w-4 text-[#5D3FD3]" />Jour de formation</span>
          <Switch checked={isTraining} onCheckedChange={setIsTraining} data-testid="add-day-training-switch" />
        </label>

        {hoursRelevant && (
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Heures travaillées</label>
            <Input type="number" step="0.25" min="0" max="24" value={hours} onChange={(e) => setHours(e.target.value)}
              className="mt-1" placeholder="Ex. 4" data-testid="add-day-hours" />
          </div>
        )}

        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-muted-ink">Enseignes trouvées <span className="font-normal normal-case">(facultatif)</span></label>
          <div className="mt-1 flex flex-wrap items-end gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nom de l'enseigne"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEns(); } }}
              className="h-9 min-w-0 flex-1" data-testid="add-day-ens-name" />
            <Input value={ville} onChange={(e) => setVille(e.target.value)} placeholder="Ville"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEns(); } }}
              className="h-9 w-24 shrink-0" data-testid="add-day-ens-ville" />
            <Button type="button" size="icon" variant="outline" className="h-9 w-9 shrink-0" onClick={addEns} data-testid="add-day-ens-add"><Plus className="h-4 w-4" /></Button>
          </div>
          {ens.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="add-day-ens-list">
              {ens.map((x, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white py-0.5 pl-2 pr-1 text-[11px] font-semibold">
                  <Store className="h-3 w-3 text-[var(--shariti-orange)]" />{x.name}{x.ville ? ` · ${x.ville}` : ""}
                  <button onClick={() => setEns((l) => l.filter((_, j) => j !== i))} className="grid h-4 w-4 place-items-center rounded-full text-muted-ink hover:bg-[rgba(214,69,69,0.15)] hover:text-[#9B2226]"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="add-day-cancel">Annuler</Button>
          <Button className="bg-[var(--shariti-orange)] font-semibold text-white hover:bg-[#E56622]" onClick={submit} disabled={saving} data-testid="add-day-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MiniStat = ({ icon: Icon, label, value, tone }) => {
  const color = tone === "ok" ? "#0F5A2F" : tone === "bad" ? "#9B2226" : "var(--shariti-ink)";
  return (
    <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border-soft)] bg-white p-2.5" data-testid={`daily-stat-${label}`}>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[var(--surface-2)] text-[var(--shariti-orange)]"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0">
        <div className="truncate text-base font-extrabold tabular-nums leading-none" style={{ color }}>{value}</div>
        <div className="text-[9px] font-semibold uppercase text-muted-ink">{label}</div>
      </div>
    </div>
  );
};

// Suffixe de localisation d'un spot : « Ville (Dept) », sans répéter la ville déjà
// contenue dans le nom de l'enseigne. Renvoie "" si aucune info de lieu.
function spotLoc(s) {
  const ville = (s?.ville || "").trim();
  const dept = (s?.dept != null ? String(s.dept) : "").trim();
  const nameHasVille = ville && (s?.enseigne || "").toUpperCase().includes(ville.toUpperCase());
  if (ville && !nameHasVille) return dept ? `${ville} (${dept})` : ville;
  return dept ? `(${dept})` : "";        // ville déjà dans le nom → seulement le département
}

// Info-bulle complète d'un spot : « Enseigne · Ville (Dept) ».
function spotTitle(s) {
  const ville = (s?.ville || "").trim();
  const dept = (s?.dept != null ? String(s.dept) : "").trim();
  const parts = [s?.enseigne || "—"];
  if (ville) parts.push(dept ? `${ville} (${dept})` : ville);
  else if (dept) parts.push(`(${dept})`);
  return parts.join(" · ");
}

// Détail complet d'une enseigne comptée : adresse, responsable ayant autorisé
// et dates prévues de passage. Rendu au clic sur le nom de l'enseigne (popover).
// Si le spot n'a aucune info détaillée (ex. saisie manuelle), on rend l'élément
// tel quel, sans popover, pour rester compact.
function SpotDetailPopover({ s, children }) {
  const villeDept = [s?.ville, s?.dept ? `(${s.dept})` : ""].filter(Boolean).join(" ");
  const addrFull = (s?.address_full || "").trim();
  const composed = [
    (s?.rue || "").trim(),
    [s?.code_postal, s?.ville].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");
  const displayAddr = addrFull || (s?.rue || s?.code_postal ? composed : "");
  const planned = Array.isArray(s?.planned_dates) ? s.planned_dates : [];
  const hasAddr = !!displayAddr;
  const hasAuth = !!s?.authorized_by;
  const hasDates = planned.length > 0 || !!s?.planned_dates_label;
  const hasDetails = hasAddr || hasAuth || hasDates;

  if (!hasDetails) return children;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded text-left underline decoration-dotted decoration-[rgba(24,24,24,0.25)] underline-offset-2 outline-none transition-colors hover:decoration-[var(--shariti-orange)] focus-visible:ring-2 focus-visible:ring-[var(--shariti-orange)]/40"
          title="Voir le détail du spot (adresse, responsable, dates de passage)"
          data-testid={`spot-detail-trigger-${s.spot_id}`}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start" side="bottom" sideOffset={6}
        className="w-[19rem] rounded-2xl border border-[var(--border-soft)] bg-white/95 p-4 shadow-xl backdrop-blur-sm"
        data-testid={`spot-detail-${s.spot_id}`}
      >
        <div className="space-y-3">
          {/* En-tête : enseigne + ville */}
          <div>
            <div className="flex items-start gap-1.5">
              <Store className="mt-0.5 h-4 w-4 shrink-0 text-[var(--shariti-orange)]" />
              <div className="min-w-0">
                <div className="break-words text-sm font-extrabold leading-tight text-ink" data-testid="spot-detail-name">
                  {s.enseigne || "—"}
                </div>
                {villeDept && <div className="mt-0.5 text-[11px] font-medium text-muted-ink">{villeDept}</div>}
              </div>
            </div>
          </div>

          {/* Adresse complète */}
          {hasAddr && (
            <div className="flex items-start gap-2 border-t border-[var(--border-soft)] pt-2.5">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Adresse</div>
                <div className="break-words text-[12px] leading-snug text-ink" data-testid="spot-detail-address">{displayAddr}</div>
              </div>
            </div>
          )}

          {/* Responsable ayant autorisé */}
          {hasAuth && (
            <div className="flex items-start gap-2 border-t border-[var(--border-soft)] pt-2.5">
              <UserCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Autorisé par</div>
                <div className="break-words text-[12px] font-semibold leading-snug text-ink" data-testid="spot-detail-authorized">
                  {s.authorized_by}{s.authorized_role ? <span className="font-normal text-muted-ink"> · {s.authorized_role}</span> : null}
                </div>
                {s.has_mandat && (
                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-[rgba(22,163,74,0.12)] px-1.5 py-0.5 text-[10px] font-bold text-[#15803D]">
                    <BadgeCheck className="h-3 w-3" />Mandat signé
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Dates prévues de passage */}
          {hasDates && (
            <div className="flex items-start gap-2 border-t border-[var(--border-soft)] pt-2.5">
              <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--shariti-orange)]" />
              <div className="min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wide text-muted-ink">Dates prévues de passage</div>
                {planned.length > 0 ? (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid="spot-detail-dates">
                    {planned.map((d) => (
                      <span key={d} className="inline-flex items-center rounded-md border border-[var(--border-soft)] bg-[var(--surface-2)] px-1.5 py-0.5 text-[11px] font-semibold capitalize text-ink">
                        {frDateFull(d)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="mt-0.5 text-[12px] font-semibold capitalize leading-snug text-ink" data-testid="spot-detail-dates-label">{s.planned_dates_label}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Infos détaillées affichées DIRECTEMENT sous le nom du spot (sans clic), pour
// TOUS les CDP : adresse de l'emplacement, responsable ayant autorisé la venue et
// dates prévues de passage. Rien n'est rendu si aucune info n'est disponible.
function SpotInlineInfo({ s }) {
  const addrFull = (s?.address_full || "").trim();
  const composed = [
    (s?.rue || "").trim(),
    [s?.code_postal, s?.ville].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");
  const displayAddr = addrFull || (s?.rue || s?.code_postal ? composed : "");
  const planned = Array.isArray(s?.planned_dates) ? s.planned_dates : [];
  const hasAuth = !!s?.authorized_by;
  const hasDates = planned.length > 0 || !!s?.planned_dates_label;
  if (!displayAddr && !hasAuth && !hasDates) return null;
  return (
    <span className="mt-0.5 flex flex-col gap-0.5 pl-4 font-normal" data-testid={`daily-spot-info-${s.spot_id}`}>
      {displayAddr && (
        <span className="flex items-start gap-1 text-[10px] leading-snug text-muted-ink" data-testid={`daily-spot-addr-${s.spot_id}`}>
          <MapPin className="mt-[1px] h-2.5 w-2.5 shrink-0 text-[var(--shariti-orange)]" />
          <span className="break-words">{displayAddr}</span>
        </span>
      )}
      {hasAuth && (
        <span className="flex items-start gap-1 text-[10px] leading-snug text-ink" data-testid={`daily-spot-auth-${s.spot_id}`}>
          <UserCheck className="mt-[1px] h-2.5 w-2.5 shrink-0 text-[var(--shariti-orange)]" />
          <span className="break-words">
            <span className="font-semibold">Autorisé par :</span> {s.authorized_by}
            {s.authorized_role ? <span className="text-muted-ink"> · {s.authorized_role}</span> : null}
            {s.has_mandat ? <span className="text-[#15803D]"> · Mandat signé</span> : null}
          </span>
        </span>
      )}
      {hasDates && (
        <span className="flex flex-wrap items-center gap-1" data-testid={`daily-spot-dates-${s.spot_id}`}>
          <CalendarDays className="h-2.5 w-2.5 shrink-0 text-muted-ink" title="Dates prévues de passage" />
          {planned.length > 0 ? (
            planned.map((d) => (
              <span key={d} className="inline-flex items-center rounded border border-[var(--border-soft)] bg-white px-1 py-0.5 text-[10px] font-semibold capitalize text-ink">
                {frDateChip(d)}
              </span>
            ))
          ) : (
            <span className="text-[10px] font-semibold capitalize text-ink">{s.planned_dates_label}</span>
          )}
        </span>
      )}
    </span>
  );
}



function DayRow({ day, cdpId, billingType, hoursRelevant = true, onSaved, onAskExclude, onAddDay, onRemoveManual }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(day.hours ?? ""));
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const dim = (!day.billable && !day.is_training && !(day.spots && day.spots.length))
    || (day.is_future && day.hours_override == null && !(day.spots && day.spots.length));

  const startEdit = () => { setVal(day.hours_override != null ? String(day.hours_override) : String(day.hours ?? "")); setEditing(true); };

  // Marque / retire ce jour comme ABSENCE → le back recalcule coût & marge (prorata forfait).
  const toggleAbsence = async () => {
    setToggling(true);
    try {
      const next = !day.is_absence;
      await api.put(`/performance/cdp-billing/cdp/${cdpId}/absence`, { date: day.date, absent: next });
      toast.success(next ? "Jour marqué absent — coût & marge recalculés" : "Jour rétabli (travaillé)");
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de mettre à jour l'absence");
    } finally { setToggling(false); }
  };
  // Éligible au marquage d'absence : jour ouvré du CDP, dans le contrat et déjà écoulé.
  const canMarkAbsent = day.is_working_day && !day.before_contract && !day.after_contract && !day.is_future;

  const submit = async (clear = false) => {
    const num = clear || val === "" ? null : Number(val);
    if (num != null && !(num >= 0 && num <= 24)) { toast.error("Heures entre 0 et 24"); return; }
    setSaving(true);
    try {
      await api.put(`/performance/cdp-billing/cdp/${cdpId}/daily-hours`, { date: day.date, hours: num });
      toast.success(clear ? "Heures réinitialisées" : "Heures mises à jour");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement des heures");
    } finally { setSaving(false); }
  };

  const metaLabel = day.is_training ? "Formation"
    : day.before_contract ? "Avant entrée"
      : day.after_contract ? "Après sortie"
        : day.is_absence ? "Absence"
          : !day.is_working_day ? "Hors rythme"
            : day.is_future ? "À venir"
              : "Travaillé";

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border bg-white px-3 py-2.5 ${day.is_training ? "border-[#5D3FD3]/40 bg-[rgba(93,63,211,0.04)]" : "border-[var(--border-soft)]"} ${dim ? "opacity-60" : ""}`} data-testid={`daily-row-${day.date}`}>
      {/* Date */}
      <div className="w-[104px] shrink-0">
        <div className="flex items-center gap-1 text-sm font-bold capitalize">
          {frShort(day.date)}
          {day.is_training && <GraduationCap className="h-3.5 w-3.5 text-[#5D3FD3]" data-testid={`daily-training-${day.date}`} />}
        </div>
        {day.is_absence ? (
          <button type="button" onClick={toggleAbsence} disabled={toggling}
            className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-[rgba(233,184,0,0.2)] px-1.5 py-0.5 text-[10px] font-bold text-[#8A6D00] transition-colors hover:bg-[rgba(233,184,0,0.34)] disabled:opacity-50"
            title="Rétablir ce jour en « travaillé » (recompte le coût)" data-testid={`daily-absence-toggle-${day.date}`}>
            {toggling ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarOff className="h-3 w-3" />}
            Absent · rétablir
          </button>
        ) : (
          <>
            <div className="text-[10px] text-muted-ink">{metaLabel}</div>
            {canMarkAbsent && (
              <button type="button" onClick={toggleAbsence} disabled={toggling}
                className="mt-0.5 inline-flex items-center gap-0.5 rounded-full border border-dashed border-[var(--border-soft)] px-1.5 py-0.5 text-[9px] font-semibold text-muted-ink transition-colors hover:border-[#E9B800] hover:bg-[rgba(233,184,0,0.12)] hover:text-[#8A6D00] disabled:opacity-50"
                title="Marquer ce jour comme absence : son coût passe à 0 € et la marge est recalculée"
                data-testid={`daily-absence-toggle-${day.date}`}>
                {toggling ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <CalendarOff className="h-2.5 w-2.5" />}
                Absent
              </button>
            )}
          </>
        )}
      </div>

      {/* Heures (éditable) — masquées pour un CDP payé à l'enseigne */}
      {hoursRelevant ? (
        <div className="flex w-[136px] shrink-0 items-center gap-1.5" data-testid={`daily-hours-${day.date}`}>
          <Clock className="h-3.5 w-3.5 text-muted-ink" />
          {editing ? (
            <>
              <Input type="number" step="0.25" min="0" max="24" value={val} onChange={(e) => setVal(e.target.value)}
                className="h-7 w-16 px-2 text-sm" autoFocus data-testid={`daily-hours-input-${day.date}`} />
              <Button size="icon" className="h-7 w-7 bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]" onClick={() => submit(false)} disabled={saving} data-testid={`daily-hours-save-${day.date}`}><Check className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-ink" onClick={() => setEditing(false)} data-testid={`daily-hours-cancel-${day.date}`}><X className="h-3.5 w-3.5" /></Button>
            </>
          ) : (
            <>
              <span className="text-sm font-bold tabular-nums">{day.hours} h</span>
              {day.hours_override != null && (
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[var(--shariti-orange)]" title="Heures modifiées manuellement" />
              )}
              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-ink hover:text-[var(--shariti-orange)]" onClick={startEdit} title="Modifier les heures" data-testid={`daily-hours-edit-${day.date}`}><Pencil className="h-3 w-3" /></Button>
              {day.hours_override != null && (
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-ink hover:text-ink" onClick={() => submit(true)} title="Revenir au rythme théorique" data-testid={`daily-hours-reset-${day.date}`}><RotateCcw className="h-3 w-3" /></Button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex w-[92px] shrink-0 items-center gap-1.5 text-[11px] font-semibold text-muted-ink" data-testid={`daily-ens-count-${day.date}`}>
          <Store className="h-3.5 w-3.5" />{day.spots_count} ens.
        </div>
      )}

      {/* Spots décrochés ce jour-là (réels, reportés, manuels) */}
      <div className="flex min-w-[160px] flex-1 flex-wrap items-start gap-1">
        {(!day.spots || day.spots.length === 0) ? (
          <span className="text-[11px] italic text-muted-ink">Aucun spot</span>
        ) : day.spots.map((s, sidx) => {
          const loc = spotLoc(s);
          const title = spotTitle(s);
          const isCrayon = s.status === "au_crayon";
          if (s.carried_over) {
            const carriedLbl = s.dup_in_month ? "déjà comptée ce mois" : "déjà comptée";
            const carriedTitle = s.dup_in_month
              ? `${title} — déjà comptée ce mois-ci (même enseigne, autre date : non recomptée)`
              : `${title} — déjà comptée un mois précédent (non recomptée)`;
            return (
              <span key={`${s.spot_id}-${sidx}`} title={carriedTitle}
                className="inline-flex max-w-[360px] flex-col gap-0.5 rounded-md border border-dashed border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-1 text-[11px] font-semibold text-muted-ink" data-testid={`daily-spot-carried-${s.spot_id}`}>
                <span className="flex items-center gap-1">
                  <Store className="h-3 w-3 shrink-0" />
                  <SpotDetailPopover s={s}>
                    <span className="break-words leading-tight line-through decoration-[rgba(24,24,24,0.35)]">
                      {s.enseigne || "—"}{loc ? ` · ${loc}` : ""}
                    </span>
                  </SpotDetailPopover>
                  {isCrayon && (
                    <span className="shrink-0 rounded-full bg-[rgba(47,111,237,0.12)] px-1 py-0.5 text-[9px] font-bold text-[#1D4ED8]" title="Booking au crayon (non confirmé)">crayon</span>
                  )}
                  <span className="ml-auto shrink-0">· {carriedLbl}</span>
                </span>
                <SpotInlineInfo s={s} />
              </span>
            );
          }
          if (s.manual) {
            return (
              <span key={`${s.spot_id}-${sidx}`} title={title} className="inline-flex max-w-[300px] items-center gap-1 rounded-md border border-[var(--shariti-orange)]/50 bg-[rgba(238,114,46,0.08)] py-0.5 pl-2 pr-1 text-[11px] font-semibold text-[#8A3A10]" data-testid={`daily-spot-manual-${s.spot_id}`}>
                <Sparkles className="h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                <span className="break-words leading-tight">
                  {s.enseigne || "—"}{loc && <span className="font-normal text-[#8A3A10]/70"> · {loc}</span>}
                </span>
                <button onClick={() => onRemoveManual && onRemoveManual(day.date, s.spot_id)}
                  className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-muted-ink hover:bg-[rgba(214,69,69,0.15)] hover:text-[#9B2226]"
                  title="Retirer cette enseigne saisie à la main" data-testid={`daily-spot-manual-delete-${s.spot_id}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          }
          return (
            <span key={`${s.spot_id}-${sidx}`} title={title} className="inline-flex max-w-[360px] flex-col gap-0.5 rounded-md border border-[var(--border-soft)] bg-[var(--surface-2)] px-2 py-1 text-[11px] font-semibold" data-testid={`daily-spot-${s.spot_id}`}>
              <span className="flex items-center gap-1">
                <Store className="h-3 w-3 shrink-0 text-[var(--shariti-orange)]" />
                <SpotDetailPopover s={s}>
                  <span className="break-words leading-tight">
                    {s.enseigne || "—"}{loc && <span className="font-normal text-muted-ink"> · {loc}</span>}
                  </span>
                </SpotDetailPopover>
                {isCrayon && (
                  <span className="shrink-0 rounded-full bg-[rgba(47,111,237,0.12)] px-1 py-0.5 text-[9px] font-bold text-[#1D4ED8]" title="Booking au crayon (non confirmé) — déjà pris en compte">crayon</span>
                )}
                <button onClick={() => onAskExclude({ spot_id: s.spot_id, enseigne: s.enseigne })}
                  className="ml-auto grid h-4 w-4 shrink-0 place-items-center rounded-full text-muted-ink hover:bg-[rgba(214,69,69,0.15)] hover:text-[#9B2226]"
                  title="Retirer ce spot du décompte (test, erroné ou magasin rétracté)" data-testid={`daily-spot-delete-${s.spot_id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
              <SpotInlineInfo s={s} />
            </span>
          );
        })}
        <button onClick={() => onAddDay && onAddDay(day.date)} title="Ajouter une enseigne / des heures à ce jour"
          className="inline-flex items-center gap-0.5 rounded-md border border-dashed border-[var(--border-soft)] px-1.5 py-0.5 text-[11px] font-semibold text-muted-ink hover:border-[var(--shariti-orange)] hover:text-[var(--shariti-orange)]" data-testid={`daily-add-to-${day.date}`}>
          <Plus className="h-3 w-3" />
        </button>
      </div>

      {/* Coût / Marge du jour */}
      <div className="ml-auto flex shrink-0 items-center gap-4 text-right">
        <div>
          <div className="text-[9px] uppercase text-muted-ink">Coût</div>
          <div className="text-xs font-semibold tabular-nums">{eur(day.cost)}</div>
        </div>
        <div>
          <div className="text-[9px] uppercase text-muted-ink">Marge</div>
          <div className="text-sm font-extrabold tabular-nums" style={{ color: day.margin >= 0 ? "#0F5A2F" : "#9B2226" }}>{eur(day.margin)}</div>
        </div>
      </div>
    </div>
  );
}

const Loader2Icon = () => (
  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
);

const MarginTile = ({ value }) => {
  const positive = (value ?? 0) >= 0;
  return (
    <div className={`${card} flex items-center gap-3 p-3`} data-testid="billing-margin-tile">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
        style={{ background: positive ? "rgba(31,157,85,0.12)" : "rgba(214,69,69,0.1)", color: positive ? "#0F5A2F" : "#B91C1C" }}>
        {positive ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
      </span>
      <div>
        <div className="text-xl font-extrabold tabular-nums" style={{ color: positive ? "#0F5A2F" : "#9B2226" }}>{eur(value)}</div>
        <div className="text-[10px] font-semibold uppercase leading-tight text-muted-ink">Marge totale</div>
      </div>
    </div>
  );
};


/* ------------------------------------------------ CONTRÔLE DES CONFIRMATIONS */

const LEVEL_META = {
  confirmed_link: { label: "Confirmé par le lien", tone: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]", dot: "#1F9D55", icon: ShieldCheck },
  ai_accord: { label: "Accord vérifié (IA)", tone: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]", dot: "#1F9D55", icon: BadgeCheck },
  contradiction: { label: "Contradiction : audio ≠ déclaré", tone: "bg-[rgba(185,28,28,0.18)] text-[#7F1D1D]", dot: "#B91C1C", icon: GitCompareArrows },
  ai_refus: { label: "Refus détecté à l'audio", tone: "bg-[rgba(214,69,69,0.2)] text-[#9B2226]", dot: "#B91C1C", icon: PhoneOff },
  declared_only: { label: "Déclaré, non vérifiable", tone: "bg-[rgba(252,219,83,0.45)] text-[#5A4200]", dot: "#E9B800", icon: HelpCircle },
  callback: { label: "À rappeler", tone: "bg-[rgba(252,219,83,0.4)] text-[#5A4200]", dot: "#E9B800", icon: PhoneOutgoing },
  no_trace: { label: "Aucune trace d'accord", tone: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]", dot: "#D64545", icon: ShieldAlert },
};
const RISK_META = {
  ok: { label: "OK", tone: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]" },
  watch: { label: "À surveiller", tone: "bg-[rgba(252,219,83,0.45)] text-[#5A4200]" },
  alert: { label: "Alerte", tone: "bg-[rgba(214,69,69,0.14)] text-[#9B2226]" },
};
const CALL_RESULT_FR = {
  oui_oral: "Oui oral", date_posee: "Date posée", refus_definitif: "Refus définitif",
  a_rappeler: "À rappeler", nrp: "N'a pas répondu",
};
// Issue détectée par l'IA à l'écoute de l'audio réel de l'appel.
const AI_OUTCOME_FR = {
  accord: "Accord", rendez_vous: "Rendez-vous", a_relancer: "À relancer",
  refus: "Refus", injoignable: "Injoignable", information: "Information", autre: "Autre",
};
const AI_OUTCOME_TONE = {
  accord: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]", rendez_vous: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
  refus: "bg-[rgba(214,69,69,0.14)] text-[#9B2226]", a_relancer: "bg-[rgba(252,219,83,0.45)] text-[#5A4200]",
  injoignable: "bg-[var(--surface-2)] text-muted-ink", information: "bg-[var(--surface-2)] text-muted-ink",
  autre: "bg-[var(--surface-2)] text-muted-ink",
};
const PERIODS = [{ v: 30, l: "30 j" }, { v: 90, l: "90 j" }, { v: 365, l: "1 an" }];

function frDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return String(iso).slice(0, 10); }
}

function ConfirmationAudit() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cdpFilter, setCdpFilter] = useState(null);
  const [levelFilter, setLevelFilter] = useState("risk"); // risk | all | <level>

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/performance/confirmations-audit", { params: { days } });
      setData(d);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  const RISK_LEVELS = ["contradiction", "ai_refus", "no_trace"];
  const cases = (data?.cases || []).filter((c) => {
    if (cdpFilter && c.cdp_id !== cdpFilter) return false;
    if (levelFilter === "all") return true;
    if (levelFilter === "risk") return RISK_LEVELS.includes(c.level);
    return c.level === levelFilter;
  });

  return (
    <div data-testid="confirmation-audit">
      {/* Bandeau explicatif */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3 rounded-[var(--radius)] border border-[var(--border-soft)] bg-[var(--surface-2)] px-4 py-3">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-white text-[var(--shariti-orange)]"><ShieldCheck className="h-4 w-4" /></span>
          <div>
            <div className="text-sm font-bold text-ink">Contrôle des confirmations — vérité audio (IA)</div>
            <p className="max-w-2xl text-xs leading-snug text-muted-ink">
              Chaque confirmation envoyée est confrontée à <strong>ce que l'IA a réellement entendu au téléphone</strong> :
              accord vérifié, ou au contraire <strong className="text-[#7F1D1D]">contradiction</strong> (le CDP annonce un « oui » que l'audio ne confirme pas) ou <strong className="text-[#9B2226]">refus détecté</strong>. Plus besoin d'écouter les appels un par un.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-[var(--border-soft)] bg-white p-1" data-testid="audit-period">
          {PERIODS.map((p) => (
            <button key={p.v} onClick={() => setDays(p.v)} data-testid={`audit-period-${p.v}`}
              className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${days === p.v ? "bg-[var(--shariti-orange)] text-white" : "text-muted-ink hover:bg-[var(--surface-2)]"}`}>{p.l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-[var(--radius)]" />)}</div>
          <Skeleton className="h-64 w-full rounded-[var(--radius)]" />
        </div>
      ) : (
        <>
          {/* KPI */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryTile icon={CalendarDays} label="Confirmations envoyées" value={k.total ?? 0} />
            <AuditTile icon={BadgeCheck} label="Accords vérifiés (IA)" value={k.verified_ai ?? 0} tone="success"
              hint={`${k.traceability_rate ?? 0}% de traçabilité`} testId="kpi-verified-ai" />
            <AuditTile icon={GitCompareArrows} label="Contradictions" value={k.contradictions ?? 0} tone="danger"
              hint="audio ≠ déclaré par le CDP" testId="kpi-contradictions" />
            <AuditTile icon={ShieldAlert} label="Refus / sans trace" value={(k.refus ?? 0) + (k.no_trace ?? 0)} tone="danger"
              hint={k.refus ? `dont ${k.refus} refus à l'audio` : null} testId="kpi-risk" />
          </div>

          {/* Classement par CDP */}
          <div className={`${card} mb-4 overflow-hidden`}>
            <div className="flex items-center justify-between border-b border-[var(--border-soft)] px-4 py-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-ink"><Users className="h-4 w-4 text-[var(--shariti-orange)]" /> Par CDP</h3>
              <div className="flex items-center gap-2">
                {(k.cdp_flagged ?? 0) > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[rgba(214,69,69,0.12)] px-2 py-0.5 text-[10px] font-bold uppercase text-[#9B2226]" data-testid="kpi-flagged">
                    <AlertTriangle className="h-3 w-3" /> {k.cdp_flagged} à surveiller
                  </span>
                )}
                {cdpFilter && (
                  <button onClick={() => setCdpFilter(null)} className="text-xs font-semibold text-[var(--shariti-orange)] hover:underline" data-testid="audit-clear-cdp">Voir tous les CDP</button>
                )}
              </div>
            </div>
            {(data?.by_cdp || []).length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-ink">Aucune confirmation sur la période.</div>
            ) : (
              <div className="divide-y divide-[var(--border-soft)]">
                {(data.by_cdp || []).map((c) => {
                  const rm = RISK_META[c.risk_level] || RISK_META.ok;
                  const active = cdpFilter === c.cdp_id;
                  return (
                    <button key={c.cdp_id} onClick={() => setCdpFilter(active ? null : c.cdp_id)}
                      data-testid={`audit-cdp-row-${c.cdp_id}`}
                      className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors ${active ? "bg-[var(--surface-2)]" : "hover:bg-[var(--surface-2)]"}`}>
                      <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: c.color }} />
                      <span className="min-w-[130px] flex-1 font-bold text-ink">{c.cdp_name}</span>
                      {c.no_activity ? (
                        <>
                          <span className="inline-flex items-center rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-bold uppercase text-muted-ink">Aucune activité</span>
                          <span className="flex-1 text-[11px] italic text-muted-ink">Aucune confirmation sur la période</span>
                          <div className="flex w-40 items-center justify-end">
                            <span className="text-xs font-bold text-muted-ink">—</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${rm.tone}`}>{c.risk_level === "alert" && <ShieldAlert className="h-3 w-3" />}{rm.label}</span>
                          <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                            <StatChip n={c.confirmed_link + c.ai_accord} tone="ok" title="Accord prouvé (lien + IA)" />
                            <StatChip n={c.declared_only} tone="warn" title="Déclaré, à vérifier" />
                            <StatChip n={c.contradiction} tone="refus" title="Contradiction audio / déclaré" />
                            <StatChip n={c.ai_refus + c.no_trace} tone="danger" title="Refus détecté / sans trace" />
                          </div>
                          <div className="flex w-40 items-center gap-2">
                            <Progress value={c.traceability_rate} className="h-1.5 flex-1" />
                            <span className="w-9 text-right text-xs font-bold tabular-nums text-muted-ink">{c.traceability_rate}%</span>
                          </div>
                        </>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cas à contrôler */}
          <div className={`${card} overflow-hidden`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-soft)] px-4 py-3">
              <h3 className="flex items-center gap-2 text-sm font-bold text-ink">
                <ShieldAlert className="h-4 w-4 text-[#D64545]" /> Confirmations à contrôler
                {cdpFilter && <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[11px] font-semibold text-muted-ink">{(data.by_cdp || []).find((x) => x.cdp_id === cdpFilter)?.cdp_name}</span>}
              </h3>
              <div className="flex flex-wrap items-center gap-1" data-testid="audit-level-filter">
                {[{ v: "risk", l: "À risque" }, { v: "contradiction", l: "Contradictions" }, { v: "ai_refus", l: "Refus" }, { v: "declared_only", l: "À vérifier" }, { v: "no_trace", l: "Sans trace" }, { v: "all", l: "Tous" }].map((f) => (
                  <button key={f.v} onClick={() => setLevelFilter(f.v)} data-testid={`audit-filter-${f.v}`}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${levelFilter === f.v ? "bg-[var(--shariti-ink)] text-white" : "bg-[var(--surface-2)] text-muted-ink hover:bg-[var(--border-soft)]"}`}>{f.l}</button>
                ))}
              </div>
            </div>
            {cases.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-[#1F9D55]" />
                <p className="text-sm font-semibold text-ink">Aucune confirmation à contrôler ici</p>
                <p className="text-xs text-muted-ink">Toutes les confirmations de ce filtre ont une preuve d'accord.</p>
              </div>
            ) : (
              <div className="divide-y divide-[var(--border-soft)]">
                {cases.map((c) => {
                  const lm = LEVEL_META[c.level] || LEVEL_META.no_trace;
                  const LIcon = lm.icon;
                  return (
                    <div key={c.ack_id} className="px-4 py-3 hover:bg-[var(--surface-2)]" data-testid={`audit-case-${c.ack_id}`}>
                      {/* Ligne 1 : identité du cas */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${lm.tone}`}><LIcon className="h-3 w-3" /> {lm.label}</span>
                        <div className="min-w-[180px] flex-1">
                          <div className="flex items-center gap-1.5 text-sm font-bold text-ink"><Store className="h-3.5 w-3.5 text-muted-ink" /> {c.spot_name}</div>
                          <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-muted-ink">
                            {c.ville && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{c.ville}</span>}
                            {c.association && <span className="rounded bg-[var(--surface-2)] px-1.5 font-semibold">{c.association}</span>}
                            {c.dates && <span>· {c.dates}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-ink">
                          <span className="h-2.5 w-2.5 rounded-full border border-black/10" style={{ background: c.color }} />{c.cdp_name}
                        </div>
                        {c.ferme_count > 0 && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(214,69,69,0.1)] px-2 py-0.5 text-[10px] font-bold text-[#9B2226]" title="Créneaux posés en Ferme sans preuve d'accord">
                            <AlertTriangle className="h-3 w-3" /> {c.ferme_count} ferme
                          </span>
                        )}
                        {c.au_crayon_count > 0 && (
                          <span className="rounded-md bg-[rgba(252,219,83,0.35)] px-2 py-0.5 text-[10px] font-bold text-[#5A4200]" title="Créneaux au crayon">{c.au_crayon_count} au crayon</span>
                        )}
                        <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-ink">
                          <span>{frDateTime(c.sent_at)}</span>
                          <Link to={`/spots/${c.spot_id}`} className="inline-flex items-center gap-1 rounded-md border border-[var(--border-soft)] bg-white px-2 py-1 font-semibold text-ink transition-colors hover:bg-[var(--surface-2)]" data-testid={`audit-open-${c.ack_id}`}>
                            Voir le spot <ExternalLink className="h-3 w-3" />
                          </Link>
                        </div>
                      </div>
                      {/* Ligne 2 : confrontation audio IA ⇄ déclaratif du CDP */}
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-dashed border-[var(--border-soft)] pt-2" data-testid={`audit-verdict-${c.ack_id}`}>
                        {c.ai_outcome ? (
                          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-bold ${AI_OUTCOME_TONE[c.ai_outcome] || "bg-[var(--surface-2)] text-muted-ink"}`} title="Ce que l'IA a réellement entendu au téléphone">
                            <Sparkles className="h-3 w-3" /> IA : {AI_OUTCOME_FR[c.ai_outcome] || c.ai_outcome}{typeof c.ai_score === "number" ? ` · ${c.ai_score}%` : ""}
                          </span>
                        ) : !c.has_recording ? (
                          <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(252,219,83,0.4)] px-2 py-0.5 text-[10px] font-bold text-[#5A4200]" title="Aucun enregistrement à analyser">
                            <MicOff className="h-3 w-3" /> Aucun enregistrement — à écouter
                          </span>
                        ) : null}
                        {c.declared_result && (
                          <span className="inline-flex items-center gap-1 rounded-md bg-[var(--surface-2)] px-2 py-0.5 text-[10px] font-semibold text-muted-ink" title="Ce que le CDP a coché manuellement">
                            <PhoneCall className="h-3 w-3" /> Coché CDP : {CALL_RESULT_FR[c.declared_result] || c.declared_result}
                          </span>
                        )}
                        {(c.ai_essentiel || []).map((pt, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-[11px] leading-tight text-muted-ink">
                            <span className="text-[var(--shariti-orange)]">•</span> {pt}
                          </span>
                        ))}
                        {!c.ai_outcome && c.has_recording && !(c.ai_essentiel || []).length && !c.declared_result && (
                          <span className="text-[11px] italic text-muted-ink">Aucune trace d'accord au téléphone.</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const AuditTile = ({ icon: Icon, label, value, tone, hint, testId }) => {
  const tones = {
    danger: { bg: "rgba(214,69,69,0.1)", fg: "#B91C1C", val: "#9B2226" },
    warn: { bg: "rgba(252,219,83,0.35)", fg: "#5A4200", val: "#5A4200" },
    success: { bg: "rgba(31,157,85,0.12)", fg: "#0F5A2F", val: "#0F5A2F" },
  };
  const t = tones[tone] || tones.warn;
  return (
    <div className={`${card} flex items-center gap-3 p-3`} data-testid={testId}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl" style={{ background: t.bg, color: t.fg }}><Icon className="h-4 w-4" /></span>
      <div>
        <div className="text-xl font-extrabold tabular-nums" style={{ color: t.val }}>{value}</div>
        <div className="text-[10px] font-semibold uppercase leading-tight text-muted-ink">{label}</div>
        {hint && <div className="text-[10px] font-semibold text-[#9B2226]">{hint}</div>}
      </div>
    </div>
  );
};

const StatChip = ({ n, tone, title }) => {
  const tones = {
    ok: "bg-[rgba(31,157,85,0.14)] text-[#0F5A2F]",
    warn: "bg-[rgba(252,219,83,0.4)] text-[#5A4200]",
    danger: "bg-[rgba(214,69,69,0.12)] text-[#9B2226]",
    refus: "bg-[rgba(185,28,28,0.16)] text-[#7F1D1D]",
  };
  if (!n) return null;
  return <span title={title} className={`grid h-6 min-w-[1.5rem] place-items-center rounded-md px-1 tabular-nums ${tones[tone]}`}>{n}</span>;
};
